import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Components
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import AddressAutocomplete from "@/components/AddressAutocomplete";

// Icons
import {
  Database,
  Upload,
  CheckCircle,
  AlertCircle,
  Plus,
  X,
  ImageIcon,
  Search,
  RefreshCw,
  Loader2,
  Home,
  MapPin,
  DollarSign,
  Building2,
  ArrowLeft,
  FolderOpen,
  Sparkles,
  CheckCircle2,
  Eye,
  Edit,
  Wand2,
  Trash2,
  Tag
} from "lucide-react";

// Hooks & Config
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";

// Constants
const PROPERTY_TYPES = [
  { value: "Residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
] as const;

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "complete", label: "Complete" },
] as const;

const PROPERTY_SOURCE_OPTIONS = [
  {
    value: "saved",
    label: "Your Saved Properties",
    description: "Select from your existing properties",
    icon: FolderOpen,
  },
  {
    value: "manual",
    label: "Add Manually",
    description: "Enter property details manually",
    icon: Upload,
  },
  {
    value: "mls",
    label: "Import from County",
    description: "Auto-populate from public records",
    icon: Database,
  },
] as const;

// Auto AI Template IDs (Template 8, 9, 10, 11)
const AUTO_AI_TEMPLATE_IDS = [8, 9, 10, 11];

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_PHOTOS = 4;
const MAX_GENERATION_ATTEMPTS = 3;
const MAX_DESCRIPTION_LENGTH = 500;
const MAX_LISTING_TAG_LENGTH = 20;
const MAX_FEATURE_NAME_LENGTH = 30;

const LOADING_MESSAGES = [
  "Connecting to property records...",
  "Searching database...",
  "Retrieving property information...",
  "Extracting property details...",
  "Loading property features...",
  "Fetching images...",
  "Almost there...",
];

interface FeatureItem {
  id?: number;
  feature_name: string;
}

// Types
interface PropertyData {
  headline: string;
  address: string;
  price: string;
  bedrooms: string;
  bathrooms: string;
  sqft: string;
  description: string;
  propertyListType: string;
  propertyType: string;
  status: string;
  is_open_house?: boolean;
  subject: string;
  features: FeatureItem[];
}

interface PhotoItem {
  id: string;
  url: string;
  file?: File;
}

interface SavedProperty {
  id: string;
  title: string;
  address: string;
  price: number;
  bedrooms: number;
  bathrooms: number | string;
  area: number;
  property_type: string;
  status: string;
  image?: string; // Main photo
  photos?: string[]; // Secondary photos array
  subject?: string;
  is_open_house?: boolean;
  description?: string;
  owner_id?: string | number; // User who created this property
  [key: string]: any; // Allow additional properties
}

interface PropertyListType {
  id: string;
  name?: string;
  list_name?: string;
  [key: string]: any;
}

interface ValidationItem {
  label: string;
  isValid: boolean;
}

const getPersistedPhotoUrl = (url?: string | null) => {
  if (typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed || trimmed.startsWith("blob:")) return "";
  return trimmed;
};

// Initial State
const initialPropertyData: PropertyData = {
  headline: "",
  address: "",
  price: "",
  bedrooms: "",
  bathrooms: "",
  sqft: "",
  description: "",
  propertyListType: "",
  propertyType: "",
  status: "",
  is_open_house: false,
  subject: "",
  features: [],
};

interface AdditionalPropertyInfo {
  city: string;
  state: string;
  zip: string;
  county: string;
  yearBuilt: string;
  lotSqft: string;
  stories: string;
  mlsStatus: string;
  daysOnMarket: string;
  estimatedValue: string;
  lastSaleDate: string;
  lastSaleAmount: string;
  listingTag: string;
}

const DESCRIPTION_LIMIT_MESSAGE = `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less.`;

const getFriendlyApiErrorMessage = (error: any, fallback: string) => {
  const responseData = error?.response?.data;

  if (!responseData) {
    return error?.message || fallback;
  }

  if (typeof responseData === "string") {
    return responseData;
  }

  if (Array.isArray(responseData?.description) && responseData.description[0]) {
    return responseData.description[0];
  }

  if (typeof responseData?.description === "string" && responseData.description) {
    return responseData.description;
  }

  if (typeof responseData?.error === "string" && responseData.error) {
    if (
      responseData.error === "OpenAI request failed" &&
      propertyDescriptionLimitLikelyExceeded(responseData?.details)
    ) {
      return DESCRIPTION_LIMIT_MESSAGE;
    }
    return responseData.error;
  }

  if (typeof responseData?.message === "string" && responseData.message) {
    return responseData.message;
  }

  return fallback;
};

const propertyDescriptionLimitLikelyExceeded = (details: unknown) => {
  if (typeof details !== "string") return false;
  const normalized = details.toLowerCase();
  return normalized.includes("500") && normalized.includes("character");
};

// ============================================
// CAMPAIGN BUILDER - PROPERTY STEP COMPONENT
// ============================================

interface PropertyStepProps {
  currentStep: number;
  handleBack: () => void;
  handlePropertySelect: (property: SavedProperty) => void;
  properties: SavedProperty[];
  loadingProperties: boolean;
  setCampaignSubject: (subject: string) => void;
  onEditProperty?: (property: SavedProperty) => void;
  onAutoAISelect?: (property: SavedProperty, templateId: number) => void;
  onPropertyUpdate?: (updatedProperty: SavedProperty) => void;
  onPropertyAdd?: (newProperty: SavedProperty) => void;
  onPropertyDelete?: (propertyId: string | number) => void;
  onRefreshProperties?: () => void;
  onEnhancedImageUpdate?: (payload: {
    id: string | number;
    originalUrl: string;
    enhancedUrl: string;
    isHero: boolean;
  }) => void;
  currentUserId?: string | number;
  userPlan?: string | null;
  getPropertyExpiry?: (
    createdAt?: string | null,
    userPlan?: string | null
  ) => { expiryDate: Date; remainingDays: number; isExpired: boolean };
  formatExpiryMessage?: (remainingDays: number) => string;
  getPropertyAvailabilityDays?: (userPlan?: string | null) => number;
}

const PropertyStep: React.FC<PropertyStepProps> = ({
  currentStep,
  handleBack,
  handlePropertySelect,
  properties,
  loadingProperties,
  setCampaignSubject,
  onEditProperty,
  onAutoAISelect,
  onPropertyUpdate,
  onPropertyAdd,
  onPropertyDelete,
  onRefreshProperties,
  onEnhancedImageUpdate,
  currentUserId,
  userPlan,
  getPropertyExpiry,
  formatExpiryMessage,
  getPropertyAvailabilityDays,
}) => {
  // ... rest of code
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  // Synchronous in-flight guard for handleAddressSearch — setIsSearchingMLS
  // is async/batched, so a double-click or double Enter before React
  // re-renders could otherwise fire two address-lookup API calls.
  const isSearchingMLSRef = useRef(false);

  // State for property source selection
  const [propertySource, setPropertySource] = useState<
    "saved" | "manual" | "mls"
  >("saved");
  const MAX_FEATURES = 10;
  // State for inline property form (manual/MLS)
  const [propertyData, setPropertyData] =
    useState<PropertyData>(initialPropertyData);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedListType, setSelectedListType] = useState<string | null>(null);
  const [isGeneratingDesc, setIsGeneratingDesc] = useState(false);
  const [generationCount, setGenerationCount] = useState(0);

  // MLS Search State
  const [mlsAddress, setMlsAddress] = useState("");
  const [isSearchingMLS, setIsSearchingMLS] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const [mlsSearchCompleted, setMlsSearchCompleted] = useState(false);
  const [isRegeneratingSubject, setIsRegeneratingSubject] = useState(false);
  const [subjectGenCount, setSubjectGenCount] = useState(0);
  const [isImprovingSubject, setIsImprovingSubject] = useState(false);
  const [isCorrectingSubjectGrammar, setIsCorrectingSubjectGrammar] =
    useState(false);
  const [isImprovingDescription, setIsImprovingDescription] = useState(false);
  const [isCorrectingDescriptionGrammar, setIsCorrectingDescriptionGrammar] =
    useState(false);
  const [viewProperty, setViewProperty] = useState<SavedProperty | null>(null);
  const [editProperty, setEditProperty] = useState<SavedProperty | null>(null);
  const [editForm, setEditForm] = useState<SavedProperty | null>(null);
  const [propertyList, setPropertyList] = useState<SavedProperty[]>(properties);

  // Enhancement Dialog States
  const [enhanceDialogOpen, setEnhanceDialogOpen] = useState(false);
  const [enhanceDialogPhoto, setEnhanceDialogPhoto] =
    useState<PhotoItem | null>(null);
  const [enhancedPreviewUrl, setEnhancedPreviewUrl] = useState<string | null>(
    null
  );
  const [isEnhancingPreview, setIsEnhancingPreview] = useState(false);
  const [enhancedPhotoIds, setEnhancedPhotoIds] = useState<Set<string>>(
    new Set()
  );
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
  const photosRef = useRef<PhotoItem[]>([]);
  const editPhotosRef = useRef<PhotoItem[]>([]);
  const mainPhotoChangedRef = useRef(false);
  const [isAutoIQLoading, setIsAutoIQLoading] = useState(false);
  const [autoIQCount, setAutoIQCount] = useState(0);
  const [manualDescriptionEntry, setManualDescriptionEntry] = useState(false);
  const MAX_AUTO_IQ_ATTEMPTS = 3;

  // Auto AI Loading State
  const [autoAILoadingPropertyId, setAutoAILoadingPropertyId] = useState<
    string | null
  >(null);
  const [originalLoaded, setOriginalLoaded] = useState(false);
  const [enhancedLoaded, setEnhancedLoaded] = useState(false);
  const [features, setFeatures] = useState<FeatureItem[]>([]);
  const [isCustomListMode, setIsCustomListMode] = useState(false);
  const [customListName, setCustomListName] = useState("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyListTypes, setLocalPropertyListTypes] = useState<PropertyListType[]>([]);
  const [isAutoAIProcessing, setIsAutoAIProcessing] = useState(false);
  const [editPhotos, setEditPhotos] = useState<PhotoItem[]>([]);
  const [editFileInputRef] = useState(useRef<HTMLInputElement>(null));
  const [isEditDragging, setIsEditDragging] = useState(false);
  const editDropZoneRef = useRef<HTMLDivElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] =
    useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const handleOpenDeleteDialog = (
    e: React.MouseEvent,
    propertyId: string | number
  ) => {
    e.stopPropagation();
    setPropertyToDelete(propertyId);
    setDeleteDialogOpen(true);
  };
  const [additionalPropertyInfo, setAdditionalPropertyInfo] = useState<AdditionalPropertyInfo>({
    city: "",
    state: "",
    zip: "",
    county: "",
    yearBuilt: "",
    lotSqft: "",
    stories: "",
    mlsStatus: "",
    daysOnMarket: "",
    estimatedValue: "",
    lastSaleDate: "",
    lastSaleAmount: "",
    listingTag: "",
  });
  const [isUpdatingProperty, setIsUpdatingProperty] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const markTouched = useCallback((field: string) => {
    setTouchedFields((prev) => new Set(prev).add(field));
  }, []);

  const markAllRequiredTouched = useCallback(() => {
    setTouchedFields(new Set([
      "headline", "address", "price", "bedrooms", "bathrooms",
      "propertyType", "subject", "description", "propertyListType",
      "photos", "features",
    ]));
  }, []);

  const fieldError = useCallback(
    (field: string, value: string | undefined) =>
      touchedFields.has(field) && !value?.trim(),
    [touchedFields]
  );

  const openDeleteDialog = useCallback((propertyId: string) => {
    setPropertyToDelete(propertyId);
    setDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setPropertyToDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!propertyToDelete) return;

    setDeleting(true);
    try {
      await axiosInstance.delete(
        `${endpoints.properties}${propertyToDelete}/`
      );

      // ✅ UPDATE LOCAL STATE IMMEDIATELY
      setPropertyList((prev) =>
        prev.filter((p) => String(p.id) !== String(propertyToDelete))
      );

      // ✅ NOTIFY PARENT COMPONENT
      if (onPropertyDelete) {
        onPropertyDelete(propertyToDelete);
      }

      // If the property being edited is the one deleted → reset edit UI
      if (editProperty?.id === propertyToDelete) {
        setEditProperty(null);
        setEditForm(null);
        setEditPhotos([]);
      }

      toast({
        title: "Property Deleted",
        description: "The property has been removed permanently.",
      });
    } catch (err: any) {
      toast({
        title: "Deletion Failed",
        description:
          err.response?.data?.message || "Could not delete the property.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
      closeDeleteDialog();
    }
  }, [
    propertyToDelete,
    editProperty,
    toast,
    closeDeleteDialog,
    onPropertyDelete, // ✅ ADD TO DEPENDENCIES
  ]);


  useEffect(() => {
    setOriginalLoaded(false);
  }, [originalImageUrl]);

  useEffect(() => {
    setEnhancedLoaded(false);
  }, [enhancedPreviewUrl]);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    editPhotosRef.current = editPhotos;
  }, [editPhotos]);

  useEffect(() => {
    setPropertyList(properties);
  }, [properties]);
  const getFullUrl = useCallback((url: string | null | undefined): string => {
    if (!url) return "";
    if (
      url.startsWith("http") ||
      url.startsWith("blob:") ||
      url.startsWith("data:")
    ) {
      return url;
    }
    const baseUrl = axiosInstance.defaults.baseURL || "";
    return `${baseUrl}${url.startsWith("/") ? "" : "/"}${url}`;
  }, []);

  const addFeature = useCallback(() => {
    if (features.length >= MAX_FEATURES) {
      toast({
        title: "Feature Limit Reached",
        description: `You can add a maximum of ${MAX_FEATURES} features per property.`,
        variant: "destructive",
      });
      return;
    }

    setFeatures((prev) => [...prev, { feature_name: "" }]);
  }, [features.length, toast]);

  const handleFeatureChange = useCallback((index: number, value: string) => {
    const limitedValue = value.slice(0, MAX_FEATURE_NAME_LENGTH);
    setFeatures((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], feature_name: limitedValue };
      return updated;
    });
  }, []);

  const handleFeatureBlur = useCallback((index: number) => {
    setFeatures((prev) => {
      const feature = prev[index];

      // If feature is empty and unsaved (no id), remove it silently
      if (!feature?.feature_name?.trim() && !feature?.id) {
        return prev.filter((_, i) => i !== index);
      }

      return prev;
    });
  }, []);

  const handleFeatureDelete = useCallback((index: number) => {
    setFeatures((prev) => {
      const featureToDelete = prev[index];
      const updated = prev.filter((_, i) => i !== index);

      // Show toast only if deleting a feature with content
      if (featureToDelete?.feature_name?.trim() || featureToDelete?.id) {
        toast({
          title: "Feature Removed",
          description: "Feature has been deleted.",
        });
      }

      return updated;
    });
  }, [toast]);


  // ✅ Load photos when editProperty changes
  useEffect(() => {
    if (editProperty) {
      setEditForm({
        ...editProperty,
        bathrooms: String(editProperty.bathrooms ?? ""),
      });

      const existingPhotos: PhotoItem[] = [];
      const addedUrls = new Set<string>();

      // Helper to get normalized path for comparison
      const getNormalizedPath = (url: string): string => {
        if (!url) return "";
        try {
          // Handle both full URLs and relative paths
          if (url.startsWith("http")) {
            const urlObj = new URL(url);
            return urlObj.pathname.toLowerCase();
          }
          return url.toLowerCase();
        } catch {
          return url.toLowerCase();
        }
      };

      // Main image (from 'image' field)
      if (editProperty.image) {
        const mainUrl = getFullUrl(editProperty.image);
        const normalizedMain = getNormalizedPath(mainUrl);

        if (mainUrl && normalizedMain && !addedUrls.has(normalizedMain)) {
          existingPhotos.push({
            id: `main_${editProperty.id}_${Date.now()}`,
            url: mainUrl,
            file: undefined,
          });
          addedUrls.add(normalizedMain);
        }
      }

      // Secondary photos (from 'photos' array)
      if (Array.isArray(editProperty.photos) && editProperty.photos.length > 0) {
        editProperty.photos.forEach((photoUrl: string, index: number) => {
          if (photoUrl && typeof photoUrl === "string") {
            const fullUrl = getFullUrl(photoUrl);
            const normalizedUrl = getNormalizedPath(fullUrl);

            // Only add if not already added (prevents duplicates)
            if (fullUrl && normalizedUrl && !addedUrls.has(normalizedUrl)) {
              existingPhotos.push({
                id: `secondary_${editProperty.id}_${index}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
                url: fullUrl,
                file: undefined,
              });
              addedUrls.add(normalizedUrl);
            }
          }
        });
      }

      setEditPhotos(existingPhotos);
    } else {
      setEditPhotos([]);
      setEditForm(null);
    }
  }, [editProperty, getFullUrl]);

  const handleEditFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        processEditFiles(files);
      }
      event.target.value = "";
    },
    [editPhotos]
  );

  const processEditFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const remainingSlots = MAX_PHOTOS - editPhotos.length;

      if (remainingSlots <= 0) {
        toast({
          title: "Maximum Photos Reached",
          description: `You can only upload up to ${MAX_PHOTOS} photos.`,
          variant: "destructive",
        });
        return;
      }

      const filesToProcess = fileArray.slice(0, remainingSlots);
      const validPhotos: PhotoItem[] = [];

      filesToProcess.forEach((file) => {
        // Validate file extension
        const allowedExt = /\.(jpe?g|png)$/i;
        if (!allowedExt.test(file.name || "")) {
          toast({
            title: "Invalid Format",
            description: `${file.name} is not supported. Only JPG, JPEG, PNG allowed.`,
            variant: "destructive",
          });
          return;
        }

        // Validate file type
        if (!file.type.startsWith("image/")) {
          toast({
            title: "Invalid File",
            description: `${file.name} is not an image file.`,
            variant: "destructive",
          });
          return;
        }

        // Validate file size
        if (file.size > MAX_IMAGE_SIZE) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          toast({
            title: "File Too Large",
            description: `${file.name} (${fileSizeMB} MB) exceeds 5 MB limit.`,
            variant: "destructive",
          });
          return;
        }

        // Create blob URL for preview
        const url = URL.createObjectURL(file);

        validPhotos.push({
          id: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          url,
          file,
        });
      });

      if (validPhotos.length > 0) {
        setEditPhotos((prev) => [...prev, ...validPhotos]);

        toast({
          title: "Photos Added",
          description: `${validPhotos.length} photo${validPhotos.length > 1 ? "s" : ""} added successfully!`,
          duration: 1000
        });
      }
    },
    [editPhotos.length, toast]
  );

  const removeEditPhoto = useCallback(
    (photoId: string) => {
      setEditPhotos((prev) => {
        const photoToRemove = prev.find((p) => p.id === photoId);

        // Revoke blob URL if it's a new file
        if (photoToRemove?.file && photoToRemove.url.startsWith("blob:")) {
          URL.revokeObjectURL(photoToRemove.url);
        }

        return prev.filter((p) => p.id !== photoId);
      });

      toast({
        title: "Photo Removed",
        description: "Photo has been removed from the property.",
      });
    },
    [toast]
  );

  const setEditAsMainPhoto = useCallback(
    (photoId: string) => {
      setEditPhotos((prev) => {
        const photoIndex = prev.findIndex((p) => p.id === photoId);

        // Already main or not found
        if (photoIndex <= 0) return prev;
        mainPhotoChangedRef.current = true;

        // Create new array with new object references to trigger re-render
        const newPhotos: PhotoItem[] = [];

        // Add the selected photo first (new main)
        newPhotos.push({ ...prev[photoIndex] });

        // Add all other photos in order
        prev.forEach((photo, index) => {
          if (index !== photoIndex) {
            newPhotos.push({ ...photo });
          }
        });

        return newPhotos;
      });

      toast({
        title: "Main Photo Updated",
        description: "Photo has been set as the main image.",
      });
    },
    [toast]
  );

  const handleEditDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditDragging(true);
  }, []);

  const handleEditDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      editDropZoneRef.current &&
      !editDropZoneRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsEditDragging(false);
    }
  }, []);

  const handleEditDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleEditDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsEditDragging(false);
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        processEditFiles(files);
      }
    },
    [processEditFiles]
  );

  // Loading message rotation effect for MLS
  useEffect(() => {
    if (!isSearchingMLS) return;

    let messageIndex = 0;
    let progress = 0;

    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % LOADING_MESSAGES.length;
      setLoadingMessage(LOADING_MESSAGES[messageIndex]);
    }, 3000);

    const progressInterval = setInterval(() => {
      progress = Math.min(progress + Math.random() * 15, 90);
      setSearchProgress(progress);
    }, 1000);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, [isSearchingMLS]);

  // Fetch property list types on mount
  useEffect(() => {
    const fetchPropertyListTypes = async () => {
      try {
        const res = await axiosInstance.get(endpoints.propertylisttype);
        if (res.data && res.data.length > 0) {
          setLocalPropertyListTypes(res.data);
        } else {
          // setLocalPropertyListTypes(DEFAULT_PROPERTY_LIST_TYPES);
        }
      } catch (error) {
        console.error("Failed to load property list types:", error);
        // setLocalPropertyListTypes(DEFAULT_PROPERTY_LIST_TYPES);
      }
    };
    fetchPropertyListTypes();
  }, []);

  const handleListTypeSelect = useCallback((value: string) => {
    if (value === "__custom__") {
      setIsCustomListMode(true);
      setCustomListName("");
      return;
    }

    setPropertyData((prev) => ({ ...prev, propertyListType: value }));
    setSelectedListType(value);
  }, []);

  const resolvePropertyListTypeId = useCallback(
    (listTypeNameOrId?: string | null): string | undefined => {
      const normalized = String(listTypeNameOrId || "").trim();
      if (!normalized) return undefined;

      const matched = localPropertyListTypes.find(
        (l) =>
          String(l.list_name).toLowerCase() === normalized.toLowerCase() ||
          String(l.id) === normalized
      );

      return matched ? String(matched.id) : undefined;
    },
    [localPropertyListTypes]
  );

  const saveCustomListType = useCallback(async () => {
    const trimmedName = customListName.trim().slice(0, MAX_LISTING_TAG_LENGTH);

    if (!trimmedName) {
      toast({
        title: "Error",
        description: "Please enter a name for your custom tag",
        variant: "destructive",
      });
      return;
    }

    // ✅ CHECK: Only allow custom tag creation if user owns the property (when editing)
    // For creating new properties, currentUserId is always the creator
    if (editProperty && editProperty.owner_id && String(editProperty.owner_id) !== String(currentUserId)) {
      toast({
        title: "Permission Denied",
        description: "You can only create custom tags for your own properties",
        variant: "destructive",
      });
      return;
    }

    // Check if already exists
    const existingList = localPropertyListTypes.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingList) {
      setPropertyData((prev) => ({ ...prev, propertyListType: existingList.list_name }));
      setSelectedListType(existingList.list_name);
      setIsCustomListMode(false);
      setCustomListName("");
      toast({
        title: "List Selected",
        description: `"${existingList.list_name}" already exists and has been selected`,
      });
      return;
    }

    setIsSavingCustomList(true);

    try {
      const response = await axiosInstance.post(endpoints.propertylisttype, {
        list_name: trimmedName,
      });

      const newListType: PropertyListType = {
        id: response.data.id,
        list_name: response.data.list_name,
      };

      // Update local list
      setLocalPropertyListTypes((prev) => [...prev, newListType]);

      // Select the new list
      setPropertyData((prev) => ({ ...prev, propertyListType: newListType.list_name }));
      setSelectedListType(newListType.list_name);

      // Reset custom mode
      setIsCustomListMode(false);
      setCustomListName("");

      toast({
        title: "Custom Tag Created",
        description: `"${newListType.list_name}" has been created and selected`,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.list_name?.[0] ||
        error?.response?.data?.error ||
        "Failed to create custom tag";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSavingCustomList(false);
    }
  }, [customListName, localPropertyListTypes, toast, editProperty, currentUserId]);

  const cancelCustomListMode = useCallback(() => {
    setIsCustomListMode(false);
    setCustomListName("");
  }, []);

  // Cleanup remaining blob URLs only when the component actually unmounts.
  // Blob URLs for active photos must stay alive across normal state updates.
  useEffect(() => {
    return () => {
      const revokeBlobUrls = (photoList: PhotoItem[]) => {
        photoList.forEach((photo) => {
          if (photo.file && photo.url.startsWith("blob:")) {
            URL.revokeObjectURL(photo.url);
          }
        });
      };

      revokeBlobUrls(photosRef.current);
      revokeBlobUrls(editPhotosRef.current);
    };
  }, []);

  // Generate unique ID
  const generateId = () =>
    `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // ==========================================
  // HANDLE AUTO AI - For Saved Properties
  // ==========================================
  const handleAutoAIClick = useCallback(
    (property: SavedProperty) => {
      if (autoAILoadingPropertyId) return;

      setAutoAILoadingPropertyId(property.id);

      setTimeout(() => {
        const randomIndex = Math.floor(
          Math.random() * AUTO_AI_TEMPLATE_IDS.length
        );
        const selectedTemplateId = AUTO_AI_TEMPLATE_IDS[randomIndex];

        if (property.subject) {
          setCampaignSubject(property.subject);
        }

        if (onAutoAISelect) {
          onAutoAISelect(property, selectedTemplateId);
        }

        setAutoAILoadingPropertyId(null);
      }, 800);
    },
    [autoAILoadingPropertyId, setCampaignSubject, onAutoAISelect, toast]
  );

const getValidationItems = useCallback((): ValidationItem[] => {
  // A listing tag is only valid once it resolves to a known, saved ID.
  // Typing a custom tag name isn't enough by itself - the checkmark button
  // must be clicked to actually save it via saveCustomListType(), otherwise
  // the property would be created with no listing tag at all.
  const resolvedId = resolvePropertyListTypeId(
    selectedListType || propertyData.propertyListType
  );
  const hasListingTag = !!resolvedId;

  return [
    { label: "Title", isValid: !!propertyData.headline },
    { label: "Address", isValid: !!propertyData.address },
    { label: "Price", isValid: !!propertyData.price },
    { label: "Bedrooms", isValid: !!propertyData.bedrooms },
    { label: "Bathrooms", isValid: !!propertyData.bathrooms },
    { label: "Description", isValid: !!propertyData.description },
    { label: "Property Type", isValid: !!propertyData.propertyType },
    { label: `Images (${photos.length}/${MAX_PHOTOS})`, isValid: photos.length >= 1 },
    { label: "Subject", isValid: !!propertyData.subject },
    {
      label: `Features (${features.filter(f => f.feature_name.trim()).length}/${MAX_FEATURES})`,
      isValid: features.filter(f => f.feature_name.trim()).length >= 1 &&
               features.filter(f => f.feature_name.trim()).length <= MAX_FEATURES
    },
    { label: "Listing Tag", isValid: hasListingTag },
  ];
}, [propertyData, photos.length, features, selectedListType, resolvePropertyListTypeId]);

  const handleAutoAIForManualEntry = useCallback(async () => {
    markAllRequiredTouched();

    const validFeatures = features
      .map((feature) => feature.feature_name.trim())
      .filter(Boolean);

    if (validFeatures.length < 1) {
      toast({
        title: "Missing Required Fields",
        description: "Please add at least one property feature.",
        variant: "destructive",
      });
      return;
    }

    const validationItems = getValidationItems();
    const missingFields = validationItems
      .filter((item) => !item.isValid)
      .map((item) => item.label);

    if (missingFields.length > 0) {
      toast({
        title: "Missing Required Fields",
        description: `Please fill: ${missingFields.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    if (propertyData.description.trim().length > MAX_DESCRIPTION_LENGTH) {
      toast({
        title: "Description too long",
        description: DESCRIPTION_LIMIT_MESSAGE,
        variant: "destructive",
      });
      return;
    }

    setIsAutoAIProcessing(true);

    try {
      const formData = new FormData();

      const title =
        propertyData.headline.trim() ||
        propertyData.address.split(",")[0].trim();
      formData.append("title", title);
      formData.append("address", propertyData.address);

      const cleanPrice = parseInt(
        propertyData.price.replace(/[^0-9.-]+/g, ""),
        10
      );
      if (!isNaN(cleanPrice)) {
        formData.append("price", cleanPrice.toString());
      }

      formData.append("bedrooms", propertyData.bedrooms);
      formData.append("bathrooms", propertyData.bathrooms);
      if (propertyData.sqft) formData.append("area", propertyData.sqft);

      formData.append("description", propertyData.description);
      formData.append("property_type", propertyData.propertyType || "family");
      formData.append("status", propertyData.status || "active");

      if (typeof propertyData.is_open_house !== "undefined") {
        formData.append(
          "is_open_house",
          propertyData.is_open_house ? "true" : "false"
        );
      }

      if (propertyData.subject.trim()) {
        formData.append("subject", propertyData.subject.trim());
      }

      const selectedListTypeName = selectedListType || propertyData.propertyListType;
      const listTypeId = resolvePropertyListTypeId(selectedListTypeName);
      if (listTypeId) {
        formData.append("propertyListType_id", String(listTypeId));
      }

      // ✅ BACKEND STRUCTURE:
      // - 'image' key → main image
      // - any other key → goes to photos array
      photos.forEach((photo, index) => {
        // Only append actual File objects (e.g. user-uploaded images).
        if (!(photo.file instanceof File)) return;

        if (index === 0) {
          formData.append("image", photo.file);
        } else {
          formData.append(`photo_${index}`, photo.file);
        }
      });

      // Create property
      // NOTE: Do NOT set Content-Type manually when sending FormData.
      // Axios will add the required multipart boundary automatically.
      const createResponse = await axiosInstance.post(endpoints.properties, formData);

      const propertyId = createResponse.data.id || createResponse.data._id;

      if (validFeatures.length > 0) {
        for (const featureName of validFeatures) {
          try {
            await axiosInstance.post(endpoints.keyfeatures, {
              feature_name: featureName,
              propetyId: propertyId,
            });
          } catch (err) {
            console.error("Failed to save feature for Auto IQ property:", err);
          }
        }
      }

      // ✅ Fetch complete property data
      const { data: serverData } = await axiosInstance.get(
        `${endpoints.properties}${propertyId}/`
      );

      const serverPhotos = Array.isArray(serverData.photos)
        ? serverData.photos
        : [];

      const propertyListTypeObject =
        serverData.propertyListType ||
        localPropertyListTypes.find(
          (l) =>
            l.list_name === selectedListType ||
            l.list_name === propertyData.propertyListType
        );

      const localPhotoUrls = photos
        .map((photo) => getPersistedPhotoUrl(photo.url))
        .filter((url) => url !== "");
      const preferredHeroImage =
        localPhotoUrls[0] || getFullUrl(serverData.image) || "";
      const preferredSecondaryPhotos =
        localPhotoUrls.length > 1
          ? localPhotoUrls.slice(1)
          : serverPhotos.map(getFullUrl);

      const newProperty: SavedProperty = {
        id: serverData.id || serverData._id,
        title: serverData.title || title,
        address: serverData.address || propertyData.address,
        price: serverData.price || cleanPrice,
        bedrooms: serverData.bedrooms || parseInt(propertyData.bedrooms),
        bathrooms: serverData.bathrooms || parseInt(propertyData.bathrooms),
        area: serverData.area || parseInt(propertyData.sqft) || 0,
        property_type: serverData.property_type || propertyData.propertyType,
        status: serverData.status || propertyData.status,
        image: preferredHeroImage,
        photos: preferredSecondaryPhotos,
        is_open_house: serverData.is_open_house ?? false,
        subject: serverData.subject || propertyData.subject.trim(),
        description: serverData.description || propertyData.description,
        propertyListType: propertyListTypeObject
          ? {
            id: propertyListTypeObject.id,
            list_name: propertyListTypeObject.list_name || "",
          }
          : undefined,
      };

      if (onPropertyAdd) {
        onPropertyAdd(newProperty);
      }

      const randomIndex = Math.floor(
        Math.random() * AUTO_AI_TEMPLATE_IDS.length
      );
      const selectedTemplateId = AUTO_AI_TEMPLATE_IDS[randomIndex];

      toast({
        title: "🎨 Property Saved & Template Selected!",
        description: `Your property has been saved.`,
      });

      const finalSubject = serverData.subject?.trim() || propertyData.subject.trim();
      if (finalSubject) {
        setCampaignSubject(finalSubject);
      }

      if (onAutoAISelect) {
        onAutoAISelect(newProperty, selectedTemplateId);
      }
    } catch (error: any) {
      console.error("Error creating property (Auto IQ):", error);
      const serverMessage = getFriendlyApiErrorMessage(
        error,
        "Failed to add property"
      );

      toast({
        title: "Error",
        description: serverMessage || "Failed to add property",
        variant: "destructive",
      });
    } finally {
      setIsAutoAIProcessing(false);
    }
  }, [
    getValidationItems,
    propertyData,
    photos,
    selectedListType,
    localPropertyListTypes,
    resolvePropertyListTypeId,
    toast,
    setCampaignSubject,
    onAutoAISelect,
    onPropertyAdd,
    markAllRequiredTouched,
  ]);

  // Handlers
  const handleInputChange = useCallback(
    (field: keyof PropertyData, value: string) => {
      setPropertyData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleAutoIQ = useCallback(async () => {
    if (isAutoIQLoading || autoIQCount >= MAX_AUTO_IQ_ATTEMPTS) return;

    const desc = propertyData.description.trim();

    if (desc.length < 10) {
      toast({
        title: "Description too short",
        description: "Please type at least 10 characters before using Auto IQ.",
        variant: "destructive",
      });
      return;
    }

    if (desc.length > MAX_DESCRIPTION_LENGTH) {
      toast({
        title: "Description too long",
        description: DESCRIPTION_LIMIT_MESSAGE,
        variant: "destructive",
      });
      return;
    }

    setIsAutoIQLoading(true);

    try {
      const descPayload = {
        mode: "property",
        description: desc,
      };

      const descResp = await axiosInstance.post(
        endpoints.generate_description,
        descPayload
      );

      const improvedDesc = descResp.data?.description?.trim();

      if (!improvedDesc) {
        throw new Error("No description returned from AI");
      }

      setPropertyData((prev) => ({
        ...prev,
        description: improvedDesc,
      }));

      const subjectPayload = {
        description: improvedDesc,
      };

      const subjectResp = await axiosInstance.post(
        endpoints.generate_subject,
        subjectPayload
      );

      const subjects = subjectResp.data?.subjects || [];

      if (subjects.length > 0) {
        setPropertyData((prev) => ({
          ...prev,
          subject: subjects[0],
        }));
      }

      toast({
        title: "✨ Auto IQ Applied!",
        description:
          "Your description was improved and a subject line was generated from it.",
      });

      setAutoIQCount((prev) => prev + 1);
    } catch (err: any) {
      console.error("Auto IQ error:", err);
      toast({
        title: "Auto IQ Failed",
        description: getFriendlyApiErrorMessage(
          err,
          "Could not generate content. Please try again."
        ),
        variant: "destructive",
      });
    } finally {
      setIsAutoIQLoading(false);
    }
  }, [isAutoIQLoading, autoIQCount, propertyData.description, toast]);

  const validatePositiveInteger = useCallback(
    (label: string, value: string): boolean => {
      if (value === "") return true;
      const num = Number(value);
      if (!Number.isInteger(num)) {
        toast({
          title: `${label} must be a whole number`,
          description: "Decimal values are not allowed.",
          variant: "destructive",
        });
        return false;
      }
      if (num < 0) {
        toast({
          title: `${label} cannot be negative`,
          description: "Please enter a positive number.",
          variant: "destructive",
        });
        return false;
      }
      return true;
    },
    [toast]
  );

  const handleIntegerChange = useCallback(
    (field: keyof PropertyData, value: string, label: string) => {
      if (value && !/^\d*$/.test(value)) {
        return;
      }
      if (value && !validatePositiveInteger(label, value)) {
        return;
      }
      setPropertyData((prev) => ({ ...prev, [field]: value }));
    },
    [validatePositiveInteger]
  );

  const handleFloatChange = useCallback(
    (field: keyof PropertyData, value: string) => {
      // Allow only one digit after decimal
      if (value && !/^\d*\.?\d?$/.test(value)) {
        return;
      }

      setPropertyData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  const handleRegenerateDescription = useCallback(async () => {
    if (isGeneratingDesc || generationCount >= MAX_GENERATION_ATTEMPTS) return;

    setIsGeneratingDesc(true);

    try {
      const resp = await axiosInstance.post(endpoints.generate_description, {
        address: propertyData.address,
        price: propertyData.price
          ? parseInt(propertyData.price.replace(/[^0-9.-]+/g, ""))
          : 0,
        headline: propertyData.headline,
        bedrooms: propertyData.bedrooms ? parseInt(propertyData.bedrooms) : 0,
        bathrooms: propertyData.bathrooms
          ? parseInt(propertyData.bathrooms)
          : 0,
        area: propertyData.sqft ? parseInt(propertyData.sqft) : 0,
        features: "",
      });

      const description = resp.data?.description?.trim();

      if (description) {
        setPropertyData((prev) => ({ ...prev, description }));
        toast({
          title: "Description Generated",
          description: "AI-powered description added!",
        });
        setGenerationCount((prev) => prev + 1);
      }
    } catch (err: any) {
      toast({
        title: "Generation Failed",
        description: err.message || "Unable to generate description",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingDesc(false);
    }
  }, [isGeneratingDesc, generationCount, propertyData, toast]);

  const getGenerateButtonText = useCallback(() => {
    if (generationCount === 0) return "Generate";
    if (generationCount < MAX_GENERATION_ATTEMPTS) return "Regenerate";
    return "Limit Reached";
  }, [generationCount]);

  const handleImproveSubject = useCallback(async () => {
    if (isImprovingSubject || !propertyData.subject?.trim()) return;

    setIsImprovingSubject(true);
    try {
      const resp = await axiosInstance.post(endpoints.improve_text, {
        text: propertyData.subject.trim(),
      });

      const improvedText = resp.data?.improved_text?.trim();
      if (improvedText) {
        setPropertyData((prev) => ({ ...prev, subject: improvedText }));
        toast({
          title: "Subject Improved",
          description: "Your subject line has been enhanced!",
        });
      }
    } catch (err: any) {
      toast({
        title: "Failed",
        description: "Could not improve subject",
        variant: "destructive",
      });
    } finally {
      setIsImprovingSubject(false);
    }
  }, [isImprovingSubject, propertyData.subject, toast]);

  const handleCorrectSubjectGrammar = useCallback(async () => {
    if (isCorrectingSubjectGrammar || !propertyData.subject?.trim()) return;

    setIsCorrectingSubjectGrammar(true);
    try {
      const resp = await axiosInstance.post(endpoints.correct_grammar, {
        text: propertyData.subject.trim(),
      });

      const correctedText = resp.data?.corrected_text?.trim();
      if (correctedText) {
        setPropertyData((prev) => ({ ...prev, subject: correctedText }));
        toast({
          title: "Grammar Corrected",
          description: "Your subject line has been corrected!",
        });
      }
    } catch (err: any) {
      toast({
        title: "Failed",
        description: "Could not correct grammar",
        variant: "destructive",
      });
    } finally {
      setIsCorrectingSubjectGrammar(false);
    }
  }, [isCorrectingSubjectGrammar, propertyData.subject, toast]);

  const handleImproveDescription = useCallback(async () => {
    if (isImprovingDescription || !propertyData.description?.trim()) return;

    setIsImprovingDescription(true);
    try {
      const resp = await axiosInstance.post(endpoints.improve_text, {
        text: propertyData.description.trim(),
      });

      const improvedText = resp.data?.improved_text?.trim();
      if (improvedText) {
        setPropertyData((prev) => ({ ...prev, description: improvedText }));
        toast({
          title: "Description Improved",
          description: "Your description has been enhanced!",
        });
      }
    } catch (err: any) {
      toast({
        title: "Failed",
        description: "Could not improve description",
        variant: "destructive",
      });
    } finally {
      setIsImprovingDescription(false);
    }
  }, [isImprovingDescription, propertyData.description, toast]);

  const handleCorrectDescriptionGrammar = useCallback(async () => {
    if (isCorrectingDescriptionGrammar || !propertyData.description?.trim())
      return;

    setIsCorrectingDescriptionGrammar(true);
    try {
      const resp = await axiosInstance.post(endpoints.correct_grammar, {
        text: propertyData.description.trim(),
      });

      const correctedText = resp.data?.corrected_text?.trim();
      if (correctedText) {
        setPropertyData((prev) => ({ ...prev, description: correctedText }));
        toast({
          title: "Grammar Corrected",
          description: "Your description has been corrected!",
        });
      }
    } catch (err: any) {
      toast({
        title: "Failed",
        description: "Could not correct grammar",
        variant: "destructive",
      });
    } finally {
      setIsCorrectingDescriptionGrammar(false);
    }
  }, [isCorrectingDescriptionGrammar, propertyData.description, toast]);

  const processFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const remainingSlots = MAX_PHOTOS - photos.length;

      if (remainingSlots <= 0) {
        toast({
          title: "Maximum Photos Reached",
          description: `You can only upload up to ${MAX_PHOTOS} photos.`,
          variant: "destructive",
        });
        return;
      }

      const filesToProcess = fileArray.slice(0, remainingSlots);
      const skippedCount = fileArray.length - filesToProcess.length;

      const validPhotos: PhotoItem[] = [];

      filesToProcess.forEach((file) => {
        // Allow only jpg, jpeg, png by extension (some files may have empty mime)
        const allowedExt = /\.(jpe?g|png)$/i;
        if (!allowedExt.test(file.name || "")) {
          toast({
            title: "Not Supported",
            description: `${file.name} is not supported. Only JPG, JPEG, PNG allowed.`,
            variant: "destructive",
          });
          return;
        }

        if (!file.type.startsWith("image/")) {
          toast({
            title: "Invalid File Type",
            description: `${file.name} is not an image file.`,
            variant: "destructive",
          });
          return;
        }

        if (file.size > MAX_IMAGE_SIZE) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          toast({
            title: "File Too Large",
            description: `${file.name} (${fileSizeMB} MB) exceeds 5 MB limit.`,
            variant: "destructive",
          });
          return;
        }

        const url = URL.createObjectURL(file);
        validPhotos.push({
          id: generateId(),
          url,
          file,
        });
      });

      if (validPhotos.length > 0) {
        setPhotos((prev) => [...prev, ...validPhotos]);
        toast({
          title: "Photos Added",
          description: `${validPhotos.length} photo${validPhotos.length > 1 ? "s" : ""
            } uploaded successfully!`,
          duration: 1000
        });
      }

      if (skippedCount > 0) {
        toast({
          title: "Some Photos Skipped",
          description: `${skippedCount} photo${skippedCount > 1 ? "s were" : " was"
            } skipped.`,
          variant: "destructive",
        });
      }
    },
    [photos.length, toast]
  );
  const validateEditPropertyForm = useCallback(() => {
    const missingFields: string[] = [];

    const getFieldValue = (id: string) => {
      const element = document.getElementById(id) as
        | HTMLInputElement
        | HTMLTextAreaElement
        | HTMLSelectElement
        | null;
      return element?.value ?? "";
    };

    const requiredFieldIds = [
      { id: "edit-subject", label: "Subject", value: getFieldValue("edit-subject") },
      { id: "edit-title", label: "Property Title", value: getFieldValue("edit-title") },
      { id: "edit-address", label: "Address", value: getFieldValue("edit-address") },
      { id: "edit-price", label: "Price", value: getFieldValue("edit-price") },
      { id: "edit-type", label: "Property Type", value: editForm?.property_type || "" },
      { id: "edit-bedrooms", label: "Bedrooms", value: getFieldValue("edit-bedrooms") },
      { id: "edit-bathrooms", label: "Bathrooms", value: getFieldValue("edit-bathrooms") },
      { id: "edit-area", label: "Area", value: getFieldValue("edit-area") },
      { id: "edit-description", label: "Description", value: getFieldValue("edit-description") },
    ];

    requiredFieldIds.forEach(({ label, value }) => {
      if (!value.trim()) {
        missingFields.push(label);
      }
    });

    if (editPhotos.length === 0) {
      missingFields.push("Property image");
    }

    if (missingFields.length > 0) {
      toast({
        title: "Missing required information",
        description: `Please fill in all required fields, including at least one property image. Missing: ${missingFields.join(", ")}.`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  }, [editPhotos.length, toast]);

  const handleUpdateProperty = useCallback(async () => {
    if (!editForm) return;

    if (!validateEditPropertyForm()) {
      return;
    }

    setIsUpdatingProperty(true);

    // Show saving toast
    toast({
      title: "Saving Property...",
      description: "Please wait while we update your property.",
    })

    try {
      // =============================================
      // STEP 1: Analyze Photos
      // =============================================

      const mainPhoto = editPhotos[0] || null;
      const secondaryPhotos = editPhotos.slice(1);

      // Check if main image changed
      const originalMainImage = editProperty?.image ? getFullUrl(editProperty.image) : null;
      const currentMainUrl = mainPhoto?.url || null;

      // Normalize URLs for comparison
      const normalizeForCompare = (url: string | null): string => {
        if (!url) return "";
        try {
          if (url.startsWith("http")) {
            return new URL(url).pathname.toLowerCase();
          }
          return url.toLowerCase();
        } catch {
          return (url || "").toLowerCase();
        }
      };

      const originalMainPath = normalizeForCompare(originalMainImage);
      const currentMainPath = normalizeForCompare(currentMainUrl);

      const mainImageIsNewFile = mainPhoto?.file != null;
      const mainImageChanged = mainPhotoChangedRef.current || originalMainPath !== currentMainPath; // ← CHANGE THIS LINE

      // =============================================
      // STEP 2: Handle Secondary Photos - Upload new files first
      // =============================================

      const finalSecondaryUrls: string[] = [];

      for (const photo of secondaryPhotos) {
        if (!photo) continue;

        if (photo.file) {
          // New file - upload to get URL
          try {
            const uploadFormData = new FormData();
            uploadFormData.append("image", photo.file);

            const uploadRes = await axiosInstance.post(
              endpoints.mls_uploads,
              uploadFormData
            );

            if (uploadRes.data?.url) {
              finalSecondaryUrls.push(getFullUrl(uploadRes.data.url));
            }
          } catch (uploadError) {
            console.error("Failed to upload secondary photo:", uploadError);
            toast({
              title: "Upload Warning",
              description: "One photo failed to upload",
              variant: "destructive",
            });
          }
        } else if (photo.url && !photo.url.startsWith("blob:")) {
          // Existing URL - keep it
          finalSecondaryUrls.push(photo.url);
        }
      }

      // =============================================
      // STEP 3: Handle Main Image Change (URL swap)
      // =============================================

      // If main image changed but it's NOT a new file, we need special handling
      // The new main is an existing URL from secondary photos
      // We need to:
      // 1. Upload/download that image as a new file, OR
      // 2. Use a different approach

      let mainImageFile: File | null = null;
      let mainImagePath: string | null = null;

      if (mainImageIsNewFile && mainPhoto?.file) {
        // Case 1: New file uploaded - use it directly
        mainImageFile = mainPhoto.file;
      } else if (mainImageChanged && mainPhoto?.url && !mainPhoto.url.startsWith("blob:")) {
        // Case 2: Existing URL swapped to main — extract path directly, no proxy needed
        try {
          const urlObj = new URL(mainPhoto.url);
          mainImagePath = urlObj.pathname;
        } catch {
          mainImagePath = mainPhoto.url;
        }
      }

      // =============================================
      // STEP 4: Build FormData
      // =============================================

      const formData = new FormData();

      // Basic fields
      formData.append("title", editForm.title || "");
      formData.append("address", editForm.address || "");
      formData.append("price", String(editForm.price || 0));
      formData.append("bedrooms", String(editForm.bedrooms || 0));
      formData.append("bathrooms", String(Number(editForm.bathrooms || 0)));
      formData.append("area", String(editForm.area || 0));
      formData.append("property_type", editForm.property_type || "Residential");
      formData.append("status", editForm.status || "active");

      if (editForm.subject) {
        formData.append("subject", editForm.subject);
      }

      if (editForm.description) {
        formData.append("description", editForm.description);
      }

      if (typeof editForm.is_open_house !== "undefined") {
        formData.append("is_open_house", editForm.is_open_house ? "true" : "false");
      }

      // =============================================
      // STEP 5: Add Image (only if changed)
      // =============================================

      if (mainImageFile) {
        formData.append("image", mainImageFile);
      } else if (mainImagePath) {
        formData.append("image", mainImagePath);
      } else if (mainImageChanged) {
        // ✅ CRITICAL FIX: Explicitly clear image field if all photos were removed
        // Send empty string to clear the image on backend
        formData.append("image", "");
      }

      // =============================================
      // STEP 6: Add Photos Array
      // =============================================

      // Filter out the current main image from secondary photos to avoid duplication
      const filteredSecondaryUrls = finalSecondaryUrls.filter((url) => {
        const urlPath = normalizeForCompare(url);
        return urlPath !== currentMainPath;
      });

      // ✅ CRITICAL FIX: Always send photos field (even if empty) to update the backend
      if (filteredSecondaryUrls.length > 0) {
        filteredSecondaryUrls.forEach((url) => {
          formData.append("photos", url);
        });
      } else {
        // Send empty string to clear photos array on backend
        // (MultipartFriendlyListField converts "" to [])
        formData.append("photos", "");
      }

      // =============================================
      // STEP 7: Send PATCH Request
      // =============================================

      await axiosInstance.patch(
        `${endpoints.properties}${editForm.id}/`,
        formData
      );

      // =============================================
      // STEP 8: Fetch Updated Data
      // =============================================

      await new Promise((resolve) => setTimeout(resolve, 500));

      const { data: serverData } = await axiosInstance.get(
        `${endpoints.properties}${editForm.id}/`
      );

      console.log("[handleUpdateProperty] Server response photos:", serverData.photos);
      console.log("[handleUpdateProperty] Server response image:", serverData.image);

      const serverPhotos = Array.isArray(serverData.photos)
        ? serverData.photos
          .map((url: string) => getFullUrl(url))
          .filter((url: string) => url && url.trim() !== "")
        : [];

      const localEditPhotoUrls = editPhotos
        .map((photo) => getPersistedPhotoUrl(photo.url))
        .filter((url) => url !== "");
      const preferredHeroImage =
        localEditPhotoUrls[0] || getFullUrl(serverData.image) || "";
      const preferredSecondaryPhotos =
        localEditPhotoUrls.length > 1
          ? localEditPhotoUrls.slice(1)
          : serverPhotos;

      const updatedProperty: SavedProperty = {
        id: String(serverData.id || serverData._id),
        title: serverData.title || "",
        address: serverData.address || "",
        price: serverData.price || 0,
        bedrooms: serverData.bedrooms || 0,
        bathrooms: serverData.bathrooms || 0,
        area: serverData.area || 0,
        property_type: serverData.property_type || "",
        status: serverData.status || "",
        image: preferredHeroImage,
        photos: preferredSecondaryPhotos,
        is_open_house: serverData.is_open_house ?? false,
        subject: serverData.subject || "",
        description: serverData.description || "",
      };

      console.log("[handleUpdateProperty] Updated property object:", {
        image: updatedProperty.image,
        photos: updatedProperty.photos,
      });

      // =============================================
      // STEP 9: Update UI State
      // =============================================

      setPropertyList((prev) =>
        prev.map((p) =>
          String(p.id) === String(updatedProperty.id) ? { ...updatedProperty } : p
        )
      );

      // ✅ CRITICAL FIX: Notify parent component immediately with updated property
      if (onPropertyUpdate) {
        console.log("[handleUpdateProperty] Calling onPropertyUpdate with:", updatedProperty);
        onPropertyUpdate({ ...updatedProperty });
      }

      toast({
        title: "✅ Property Updated",
        description: "All changes have been saved successfully.",
      });

      setEditProperty(null);
      setEditPhotos([]);
      setEditForm(null);
    } catch (error: any) {
      console.error("=== UPDATE ERROR ===", error);
      console.error("Response data:", error.response?.data);

      // Better error message extraction
      let errorMessage = "Failed to update property. Please try again.";

      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data === "string") {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.image) {
          errorMessage = `Image error: ${Array.isArray(data.image) ? data.image.join(", ") : data.image}`;
        } else {
          // Try to extract first error
          const firstKey = Object.keys(data)[0];
          if (firstKey && data[firstKey]) {
            errorMessage = `${firstKey}: ${Array.isArray(data[firstKey]) ? data[firstKey].join(", ") : data[firstKey]}`;
          }
        }
      }

      toast({
        title: "❌ Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUpdatingProperty(false); // ← ADD THIS ENTIRE FINALLY BLOCK
    }
  }, [editForm, editPhotos, editProperty, toast, onPropertyUpdate, getFullUrl]);

  const handleDeleteProperty = useCallback(async () => {
    if (!propertyToDelete) return;

    try {
      // =============================================
      // STEP 1: Call delete API
      // =============================================

      setDeleting(true);

      await axiosInstance.delete(
        endpoints.delete_property(propertyToDelete)
      );

      // =============================================
      // STEP 2: Update local property list
      // =============================================

      setPropertyList((prev) =>
        prev.filter(
          (p) => String(p.id) !== String(propertyToDelete)
        )
      );

      // =============================================
      // STEP 3: Reset edit state if deleted property was open
      // =============================================

      if (
        editForm &&
        String(editForm.id) === String(propertyToDelete)
      ) {
        setEditForm(null);
        setEditPhotos([]);
        setEditProperty(null);
      }

      // Reset main property form
      setPropertyData(initialPropertyData);
      setPhotos([]);

      // =============================================
      // STEP 4: Success feedback
      // =============================================

      toast({
        title: "🗑️ Property Deleted",
        description: "Property deleted successfully",
      });

      // =============================================
      // STEP 5: Close confirmation dialog
      // =============================================

      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
    } catch (error: any) {
      console.error("=== DELETE PROPERTY ERROR ===", error);
      console.error("Response:", error.response?.data);

      toast({
        title: "❌ Delete Failed",
        description:
          error.response?.data?.message || "Failed to delete property",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  }, [
    propertyToDelete,
    editForm,
    toast,
    initialPropertyData,
  ]);

  const handleEnhanceImage = useCallback(
    async (photo: PhotoItem) => {
      if (!photo.file || enhancedPhotoIds.has(photo.id)) return;

      const capturedUrl = photo.url;

      setOriginalImageUrl(capturedUrl);
      setEnhanceDialogPhoto({ ...photo });
      setEnhancedPreviewUrl(null);
      setIsEnhancingPreview(true);
      setEnhanceDialogOpen(true);

      try {
        const formData = new FormData();
        formData.append("image", photo.file);

        const response = await axiosInstance.post(
          endpoints.enhance_image,
          formData
        );

        if (response.data?.success && response.data?.enhanced_image) {
          setEnhancedPreviewUrl(response.data.enhanced_image);

          // optional: mark as enhanced
          setEnhancedPhotoIds((prev) => new Set(prev).add(photo.id));
        } else {
          throw new Error("Enhancement failed - no URL returned");
        }
      } catch (error: any) {
        console.error("Enhancement error:", error);
        toast({
          title: "Enhancement Failed",
          description: error.response?.data?.error || "Please try again later.",
          variant: "destructive",
        });

        setEnhanceDialogOpen(false);
        setEnhanceDialogPhoto(null);
        setOriginalImageUrl(null);
        setEnhancedPreviewUrl(null);
      } finally {
        setIsEnhancingPreview(false);
      }
    },
    [enhancedPhotoIds, toast]
  );

  const handleApplyEnhancement = useCallback(() => {
    if (!enhanceDialogPhoto || !enhancedPreviewUrl) return;

    const isEditing = editProperty !== null;
    const isMainPhoto = isEditing
      ? editPhotos[0]?.id === enhanceDialogPhoto.id
      : photos[0]?.id === enhanceDialogPhoto.id;

    if (isEditing) {
      setEditPhotos((prev) =>
        prev.map((p) =>
          p.id === enhanceDialogPhoto.id ? { ...p, url: enhancedPreviewUrl } : p
        )
      );
    } else {
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === enhanceDialogPhoto.id ? { ...p, url: enhancedPreviewUrl } : p
        )
      );
    }

    if (onEnhancedImageUpdate) {
      onEnhancedImageUpdate({
        id: enhanceDialogPhoto.id,
        originalUrl: enhanceDialogPhoto.url,
        enhancedUrl: enhancedPreviewUrl,
        isHero: isMainPhoto,
      });
    }

    setEnhancedPhotoIds((prev) => new Set(prev).add(enhanceDialogPhoto.id));

    toast({
      title: "Image Enhanced ✨",
      description: "AI enhancement applied successfully!",
    });

    setEnhanceDialogOpen(false);
    setEnhanceDialogPhoto(null);
    setOriginalImageUrl(null);
    setEnhancedPreviewUrl(null);
  }, [enhanceDialogPhoto, enhancedPreviewUrl, editProperty, toast, editPhotos, onEnhancedImageUpdate, photos]);

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
      event.target.value = "";
    },
    [processFiles]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      dropZoneRef.current &&
      !dropZoneRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    },
    [processFiles]
  );

  const removePhoto = useCallback(
    (photoId: string) => {
      setPhotos((prev) => {
        const photoToRemove = prev.find((p) => p.id === photoId);
        if (photoToRemove) {
          URL.revokeObjectURL(photoToRemove.url);
        }
        return prev.filter((p) => p.id !== photoId);
      });
      toast({
        title: "Photo Removed",
        description: "Photo has been removed.",
      });
    },
    [toast]
  );

  const setAsMainPhoto = useCallback(
    (photoId: string) => {
      setPhotos((prev) => {
        const photoIndex = prev.findIndex((p) => p.id === photoId);
        if (photoIndex <= 0) return prev;
        const newPhotos = [...prev];
        const [photo] = newPhotos.splice(photoIndex, 1);
        newPhotos.unshift(photo);
        return newPhotos;
      });
      toast({
        title: "Main Photo Updated",
        description: "Photo has been set as the main image.",
      });
    },
    [toast]
  );

  const handleResetForm = useCallback(() => {
    setPropertyData(initialPropertyData);
    setPhotos([]);
    setMlsAddress("");
    setMlsSearchCompleted(false);
    setGenerationCount(0);
    setSubjectGenCount(0);
    setIsRegeneratingSubject(false);
    setFeatures([]);
    setSelectedListType("");
    setIsCustomListMode(false);
    setCustomListName("");
    setTouchedFields(new Set());

    // ✅ Clear additional property info
    setAdditionalPropertyInfo({
      city: "",
      state: "",
      zip: "",
      county: "",
      yearBuilt: "",
      lotSqft: "",
      stories: "",
      mlsStatus: "",
      daysOnMarket: "",
      estimatedValue: "",
      lastSaleDate: "",
      lastSaleAmount: "",
      listingTag: "",
    });
  }, []);

  const handleAddPropertyAndSelect = useCallback(async () => {
    markAllRequiredTouched();

    const validFeatures = features
      .map((feature) => feature.feature_name.trim().slice(0, MAX_FEATURE_NAME_LENGTH))
      .filter(Boolean);

    if (validFeatures.length < 1) {
      toast({
        title: "Missing Required Fields",
        description: "Please add at least one property feature.",
        variant: "destructive",
      });
      return;
    }

    if (validFeatures.length > MAX_FEATURES) {
      toast({
        title: "Feature Limit Reached",
        description: `You can add a maximum of ${MAX_FEATURES} features per property.`,
        variant: "destructive",
      });
      return;
    }

    const validationItems = getValidationItems();
    const missingFields = validationItems
      .filter((item) => !item.isValid)
      .map((item) => item.label);

    if (missingFields.length > 0) {
      toast({
        title: "Missing Required Fields",
        description: `Please fill: ${missingFields.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    if (propertyData.description && propertyData.description.length > 500) {
      toast({
        title: "Description Too Long",
        description: "Description must be 500 characters or less.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      const formData = new FormData();

      const title =
        propertyData.headline.trim() ||
        propertyData.address.split(",")[0].trim();
      formData.append("title", title);
      formData.append("address", propertyData.address);

      const cleanPrice = parseInt(
        propertyData.price.replace(/[^0-9.-]+/g, ""),
        10
      );
      if (!isNaN(cleanPrice)) {
        formData.append("price", cleanPrice.toString());
      }

      formData.append("bedrooms", propertyData.bedrooms);
      formData.append("bathrooms", propertyData.bathrooms);
      if (propertyData.sqft) formData.append("area", propertyData.sqft);

      formData.append("description", propertyData.description);
      formData.append("property_type", propertyData.propertyType || "family");
      formData.append("status", propertyData.status || "active");

      if (typeof propertyData.is_open_house !== "undefined") {
        formData.append(
          "is_open_house",
          propertyData.is_open_house ? "true" : "false"
        );
      }

      if (propertyData.subject.trim()) {
        formData.append("subject", propertyData.subject.trim());
      }

      const selectedListTypeName = selectedListType || propertyData.propertyListType;
      const listTypeId = resolvePropertyListTypeId(selectedListTypeName);
      if (listTypeId) {
        formData.append("propertyListType_id", String(listTypeId));
      }

      photos.forEach((photo, index) => {
        if (!photo.file) return;

        if (index === 0) {
          formData.append("image", photo.file);
        } else {
          formData.append(`photo_${index}`, photo.file);
        }
      });

      // Create property
      const createResponse = await axiosInstance.post(
        endpoints.properties,
        formData
      );

      const propertyId = createResponse.data.id || createResponse.data._id;

      if (validFeatures.length > 0) {
        for (const featureName of validFeatures) {
          try {
            await axiosInstance.post(endpoints.keyfeatures, {
              feature_name: featureName,
              propetyId: propertyId,
            });
          } catch (err) {
            console.error("Failed to save feature:", err);
          }
        }
      }

      // Fetch complete property data
      const { data: serverData } = await axiosInstance.get(
        `${endpoints.properties}${propertyId}/`
      );

      const serverPhotos = Array.isArray(serverData.photos)
        ? serverData.photos
        : [];

      const propertyListTypeObject =
        serverData.propertyListType ||
        localPropertyListTypes.find(
          (l) => l.list_name === selectedListTypeName
        );

      const localPhotoUrls = photos
        .map((photo) => getPersistedPhotoUrl(photo.url))
        .filter((url) => url !== "");
      const preferredHeroImage =
        localPhotoUrls[0] || getFullUrl(serverData.image) || "";
      const preferredSecondaryPhotos =
        localPhotoUrls.length > 1
          ? localPhotoUrls.slice(1)
          : serverPhotos.map(getFullUrl);

      const newProperty: SavedProperty = {
        id: serverData.id || serverData._id,
        title: serverData.title || title,
        address: serverData.address || propertyData.address,
        price: serverData.price || cleanPrice,
        bedrooms: serverData.bedrooms || parseInt(propertyData.bedrooms),
        bathrooms: serverData.bathrooms || parseInt(propertyData.bathrooms),
        area: serverData.area || parseInt(propertyData.sqft) || 0,
        property_type: serverData.property_type || propertyData.propertyType,
        status: serverData.status || propertyData.status,
        image: preferredHeroImage,
        photos: preferredSecondaryPhotos,
        is_open_house: serverData.is_open_house ?? false,
        subject: serverData.subject || propertyData.subject.trim(),
        description: serverData.description || propertyData.description,
        propertyListType: propertyListTypeObject
          ? {
            id: propertyListTypeObject.id,
            list_name: propertyListTypeObject.list_name || "",
          }
          : undefined,
      };

      // ✅ UPDATE LOCAL STATE IMMEDIATELY
      setPropertyList((prev) => [newProperty, ...prev]);

      // ✅ NOTIFY PARENT COMPONENT
      if (onPropertyAdd) {
        onPropertyAdd(newProperty);
      }

      toast({
        title: "✅ Property Added",
        description: "Property added successfully!",
        duration: 1000
      });

      const finalSubject = serverData.subject?.trim() || propertyData.subject.trim();
      if (finalSubject) {
        setCampaignSubject(finalSubject);
      }

      // Reset form after successful add
      handleResetForm();

      // Switch to saved properties view to show the new property
      setPropertySource("saved");

      handlePropertySelect(newProperty);
    } catch (error: any) {
      console.error("CREATE PROPERTY FAILED:", error);
      toast({
        title: "Error",
        description: error.response?.data?.detail
          || error.response?.data?.non_field_errors?.[0]
          || error.response?.data?.message
          || "Failed to create property",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    getValidationItems,
    propertyData,
    photos,
    selectedListType,
    localPropertyListTypes,
    features,
    toast,
    handlePropertySelect,
    setCampaignSubject,
    handleResetForm,
    getFullUrl,
    onPropertyAdd, // ✅ ADD TO DEPENDENCIES
    resolvePropertyListTypeId,
    markAllRequiredTouched,
  ]);

  // ✅ Map property type from API to your dropdown values
  const mapPropertyType = (apiPropertyType: string | undefined): string => {
    if (!apiPropertyType) return "Residential";

    const typeMap: Record<string, string> = {
      "Single Family Residence": "Residential",
      "SFR": "Residential",
      "Single Family": "Residential",
      "Multi-Family Residence": "Residential",
      "MFR": "Residential",
      "Condominium": "Residential",
      "Condo": "Residential",
      "Townhouse": "Residential",
      "Commercial": "commercial",
      "Land": "Residential",
    };

    return typeMap[apiPropertyType] || "Residential";
  };

  // ✅ Generate default description if API doesn't provide one
  const generateDefaultDescription = (apiData: any): string => {
    const parts: string[] = [];

    if (apiData.propertyType) {
      parts.push(apiData.propertyType);
    }

    if (apiData.bedrooms && apiData.bathrooms) {
      parts.push(`${apiData.bedrooms} bed, ${apiData.bathrooms} bath`);
    }

    if (apiData.sqft) {
      parts.push(`${parseInt(apiData.sqft).toLocaleString()} sq ft`);
    }

    if (apiData.yearBuilt) {
      parts.push(`built in ${apiData.yearBuilt}`);
    }

    if (parts.length === 0) {
      return "Property details imported. Please add additional information.";
    }

    return parts.join(". ") + ".";
  };

  // ✅ Generate unique ID for photos
  // const generateId = (): string => {
  //   return `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  // };
  const handleAddressSearch = useCallback(async () => {
    if (isSearchingMLSRef.current) return;

    if (!mlsAddress.trim()) {
      toast({
        title: "Error",
        description: "Please enter the property address",
        variant: "destructive",
      });
      return;
    }

    isSearchingMLSRef.current = true;
    setIsSearchingMLS(true);
    setSearchProgress(0);
    setLoadingMessage(LOADING_MESSAGES[0]);
    setMlsSearchCompleted(false);

    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axiosInstance.post(
        endpoints.address_lookup,
        { address: mlsAddress.trim() },
        { headers }
      );


      if (!response.data?.success) {
        throw new Error(response.data?.error || "Property not found");
      }

      // ✅ Extract data from new API structure
      const apiData = response.data.data || {};
      const photoSource = response.data.photo_source || null;
      const photoList = response.data.photos || [];
      const photoCount = response.data.photo_count || 0;


      // ✅ Map API response to propertyData state
      setPropertyData((prev) => ({
        ...prev,
        // Property Title
        headline: apiData.propertyTitle || apiData.address?.split(",")[0]?.trim() || "",

        // Address
        address: apiData.address || mlsAddress.trim(),

        // Price (API returns as string, keep as string for form input)
        price: apiData.price || "",

        // Bedrooms
        bedrooms: apiData.bedrooms || "",

        // Bathrooms
        bathrooms: apiData.bathrooms || "",

        // Square Feet
        sqft: apiData.sqft || "",

        // Property Type
        propertyType: mapPropertyType(apiData.propertyType),

        // Status
        status: apiData.mlsActive ? "active" : (apiData.mlsStatus?.toLowerCase() || "active"),

        // Subject Line
        subject: apiData.subject || `New Listing: ${apiData.propertyTitle || "Property"}`,

        // Description
        description: apiData.description || generateDefaultDescription(apiData),

        // Open House
        is_open_house: apiData.openHouse || false,

        // Listing Tag (store for display)
        // propertyListType: apiData.listingTag || "",
      }));

      // ✅ Handle Photos
      if (photoList.length > 0) {
        const photoItems: PhotoItem[] = photoList.map((url: string) => ({
          id: generateId(),
          url: url,
          file: undefined, // URL from API, not a file
        }));

        setPhotos(photoItems);

        // Show appropriate toast based on photo source
        if (photoSource === "google") {
          toast({
            title: "✅ Property Loaded with Google Photos",
            description: (
              <div className="space-y-1">
                <p>Property details imported successfully.</p>
                <p className="text-amber-600 font-medium">
                  📸 {photoCount} photos loaded from Google (MLS had no images)
                </p>
                <p className="text-muted-foreground text-xs">
                  You can upload custom photos or use these
                </p>
              </div>
            ),
            duration: 8000,
          });
        } else if (photoSource === "api" || photoSource === "mls") {
          toast({
            title: "✅ Property Details Imported",
            description: `Property information and ${photoCount} photos loaded from records.`,
            duration: 6000,
          });
        }
      } else {
        // No photos available
        setPhotos([]);
        toast({
          title: "✅ Property Details Imported",
          description: (
            <div className="space-y-1">
              <p>Basic property information has been loaded.</p>
              <p className="text-amber-600 font-medium">
                ⚠️ No photos available - please upload manually
              </p>
            </div>
          ),
          duration: 6000,
        });
      }

      // ✅ Store additional data for display (optional)
      // You can create a separate state for extra info
      setAdditionalPropertyInfo({
        city: apiData.city || "",
        state: apiData.state || "",
        zip: apiData.zip || "",
        county: apiData.county || "",
        yearBuilt: apiData.yearBuilt || "",
        lotSqft: apiData.lotSqft || "",
        stories: apiData.stories || "",
        mlsStatus: apiData.mlsStatus || "",
        daysOnMarket: apiData.daysOnMarket || "",
        estimatedValue: apiData.estimatedValue || "",
        lastSaleDate: apiData.lastSaleDate || "",
        lastSaleAmount: apiData.lastSaleAmount || "",
        listingTag: apiData.listingTag || "",
      });

      setSearchProgress(100);
      setMlsSearchCompleted(true);

    } catch (error: any) {
      console.error("Address lookup error:", error);
      setPhotos([]);

      toast({
        title: "Search Failed",
        description: error.response?.data?.error || error.message || "Unable to retrieve property data.",
        variant: "destructive",
      });
    } finally {
      setIsSearchingMLS(false);
      setSearchProgress(0);
      isSearchingMLSRef.current = false;
    }
  }, [mlsAddress, toast]);

  const handleRegenerateSubject = useCallback(async () => {
    if (isRegeneratingSubject || subjectGenCount >= MAX_GENERATION_ATTEMPTS)
      return;

    setIsRegeneratingSubject(true);

    try {
      const resp = await axiosInstance.post(endpoints.generate_subject, {
        address: propertyData.address,
        price: propertyData.price
          ? parseInt(propertyData.price.replace(/[^0-9.-]+/g, ""))
          : 0,
        headline: propertyData.headline,
        bedrooms: propertyData.bedrooms ? parseInt(propertyData.bedrooms) : 0,
        bathrooms: propertyData.bathrooms
          ? parseInt(propertyData.bathrooms)
          : 0,
        area: propertyData.sqft ? parseInt(propertyData.sqft) : 0,
      });

      const subjects = resp.data?.subjects || [];
      if (subjects.length > 0) {
        const newSubject = subjects[0];
        setPropertyData((prev) => ({ ...prev, subject: newSubject }));
        toast({
          title: "Subject Generated",
          description: "AI-powered subject line added!",
        });
        setSubjectGenCount((prev) => prev + 1);
      }
    } catch (err: any) {
      toast({
        title: "Generation Failed",
        description: "Could not generate subject line",
        variant: "destructive",
      });
    } finally {
      setIsRegeneratingSubject(false);
    }
  }, [isRegeneratingSubject, subjectGenCount, propertyData, toast]);



  const handleSourceChange = useCallback(
    (value: string) => {
      setPropertySource(value as "saved" | "manual" | "mls");
      handleResetForm();
    },
    [handleResetForm]
  );

  // ==========================================
  // RENDER COMPONENTS
  // ==========================================

  // MLS Search Loading Overlay
  const MLSSearchLoader = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <Card className="w-full max-w-md mx-4 shadow-2xl border-0">
        <CardContent className="pt-8 pb-8">
          <div className="flex flex-col items-center space-y-6">
            <div className="relative">
              <div className="h-20 w-20 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Database className="h-8 w-8 text-primary animate-pulse" />
              </div>
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold text-foreground">
                Searching Property Records
              </h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Looking up "
                <span className="font-medium text-foreground">
                  {mlsAddress}
                </span>
                "...
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-primary">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="animate-pulse">{loadingMessage}</span>
            </div>

            <div className="w-full space-y-2">
              <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500 ease-out"
                  style={{ width: `${searchProgress}%` }}
                />
              </div>
              <p className="text-xs text-center text-muted-foreground">
                This may take 15-30 seconds
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ==========================================
  // KEY FEATURES SECTION UI
  // ==========================================
  const KeyFeaturesSection = useMemo(() => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Key Features
          </Label>
          <p className="mt-1 text-sm text-muted-foreground">
            Add up to {MAX_FEATURES} key features for your property
          </p>
        </div>
        <span className={`text-sm font-medium ${features.length >= MAX_FEATURES ? "text-orange-600" : "text-muted-foreground"
          }`}>
          {features.filter(f => f.feature_name.trim()).length}/{MAX_FEATURES}
        </span>
      </div>

      {/* Features List */}
      <div className="space-y-2">
        {features.map((feature, index) => (
          <div
            key={feature.id || `new-${index}`}
            className="flex items-center gap-3 group"
          >
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xs font-medium text-primary">{index + 1}</span>
            </div>

            <Input
              type="text"
              value={feature.feature_name}
              onChange={(e) => handleFeatureChange(index, e.target.value)}
              autoFocus={index === features.length - 1 && !feature.feature_name && !feature.id}
              maxLength={MAX_FEATURE_NAME_LENGTH}
              placeholder="Feature name (max 30 chars)..."
              className="flex-1"
            />

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onMouseDown={(e) => {
                e.preventDefault();
                handleFeatureDelete(index);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                handleFeatureDelete(index);
              }}
              className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Add Feature Button */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addFeature}
        disabled={features.length >= MAX_FEATURES}
        className={`w-full border-dashed ${touchedFields.has("features") && features.filter(f => f.feature_name.trim()).length === 0 ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)] text-red-500 hover:text-red-600" : ""}`}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Feature
        {features.length >= MAX_FEATURES && (
          <span className="ml-2 text-xs text-muted-foreground">(Limit reached)</span>
        )}
      </Button>

      {/* Feature Suggestions */}
      {(() => {
        const allSuggestions = [
          "Hardwood Floors",
          "Updated Kitchen",
          "Central A/C",
          "Attached Garage",
          "Fenced Yard",
          "Smart Home",
        ];
        const addedFeatureNames = new Set(
          features.map((f) => (f.feature_name || "").trim().toLowerCase())
        );
        const availableSuggestions = allSuggestions.filter(
          (suggestion) => !addedFeatureNames.has(suggestion.toLowerCase())
        );

        return availableSuggestions.length > 0 ? (
          <div className="p-4 bg-muted/30 rounded-lg border border-dashed">
            <p className="text-sm text-muted-foreground mb-3">
              💡 Suggested features to add:
            </p>
            <div className="flex flex-wrap gap-2">
              {availableSuggestions.map((suggestion) => (
                <Button
                  key={suggestion}
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    if (features.length < MAX_FEATURES) {
                      setFeatures((prev) => [...prev, { feature_name: suggestion }]);
                    }
                  }}
                  className="text-xs"
                >
                  + {suggestion}
                </Button>
              ))}
            </div>
          </div>
        ) : null;
      })()}
    </div>
  ), [features, addFeature, handleFeatureChange, handleFeatureBlur, handleFeatureDelete, touchedFields]);

  // ==========================================
  // PROPERTY LIST TYPE SELECTOR UI
  // ==========================================
  const PropertyListTypeSelector = useMemo(() => (
    <div className="space-y-2">
      <Label htmlFor="propertyListType" className="flex items-center gap-2">
        <Tag className="h-4 w-4" />
        Listing Tag <span className="text-500">*</span>
      </Label>

      {isCustomListMode ? (
        // Custom list input mode
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={customListName}
            onChange={(e) => setCustomListName(e.target.value.slice(0, MAX_LISTING_TAG_LENGTH))}
            placeholder="Custom tag (max 20 chars)"
            maxLength={MAX_LISTING_TAG_LENGTH}
            className="flex-1"
            autoFocus
            disabled={isSavingCustomList}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isSavingCustomList) {
                e.preventDefault();
                saveCustomListType();
              }
              if (e.key === "Escape") {
                cancelCustomListMode();
              }
            }}
          />
          <Button
            type="button"
            size="icon"
            onClick={saveCustomListType}
            disabled={!customListName.trim() || isSavingCustomList}
            className="flex-shrink-0"
          >
            {isSavingCustomList ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={cancelCustomListMode}
            disabled={isSavingCustomList}
            className="flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        // Normal selector mode
        <Select
          value={selectedListType || propertyData.propertyListType}
          onValueChange={(v) => { handleListTypeSelect(v); markTouched("propertyListType"); }}
        >
          <SelectTrigger
            id="propertyListType"
            onBlur={() => markTouched("propertyListType")}
            className={touchedFields.has("propertyListType") && !(selectedListType || propertyData.propertyListType) ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}
          >
            <SelectValue placeholder="Select listing tag" />
          </SelectTrigger>
          <SelectContent>
            {localPropertyListTypes
              .filter(list => list.list_name !== "Open House") // Filter if needed
              .map((list) => (
                <SelectItem key={list.id} value={list.list_name}>
                  {list.list_name}
                </SelectItem>
              ))}
            {/* ✅ Show custom tag option only if user owns the property (or creating new property) */}
            {!editProperty || (editProperty.owner_id && String(editProperty.owner_id) === String(currentUserId)) ? (
              <SelectItem value="__custom__" className="font-medium text-primary">
                + Add Custom Tag
              </SelectItem>
            ) : (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                Only property owner can add custom tags
              </div>
            )}
          </SelectContent>
        </Select>
      )}

      <p className="text-xs text-muted-foreground">
        This tag will appear on your property marketing materials
      </p>
    </div>
  ), [
    isCustomListMode,
    customListName,
    isSavingCustomList,
    selectedListType,
    propertyData.propertyListType,
    localPropertyListTypes,
    handleListTypeSelect,
    saveCustomListType,
    cancelCustomListMode,
    editProperty,
    currentUserId,
    touchedFields,
    markTouched,
  ]);

  // Property Source Selector (Radio Buttons)
  const PropertySourceSelector = useMemo(
    () => (
      <div className="space-y-3">
        <Label className="text-base font-medium">
          How would you like to add a property?
        </Label>
        <RadioGroup
          value={propertySource}
          onValueChange={handleSourceChange}
          className="grid w-full gap-3 sm:grid-cols-3"
        >
          {PROPERTY_SOURCE_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = propertySource === option.value;
            return (
              <label
                key={option.value}
                htmlFor={option.value}
                className={`
                relative flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 p-3 text-center transition-all duration-200
                ${isSelected
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border bg-background hover:border-primary/40 hover:bg-muted/30"
                  }
              `}
              >
                <RadioGroupItem
                  value={option.value}
                  id={option.value}
                  className="sr-only"
                />
                <div
                  className={`
                  flex h-10 w-10 items-center justify-center rounded-lg transition-colors
                  ${isSelected
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                    }
                `}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <div className="space-y-1">
                  <p
                    className={`font-semibold leading-tight ${isSelected ? "text-primary" : "text-foreground"
                      }`}
                  >
                    {option.label}
                  </p>
                  <p className="text-xs text-muted-foreground leading-snug">
                    {option.description}
                  </p>
                </div>
                {isSelected && (
                  <div className="absolute right-2 top-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                  </div>
                )}
              </label>
            );
          })}
        </RadioGroup>
      </div>
    ),

    [propertySource, handleSourceChange]
  );

  // Saved Properties List with plan-aware availability filtering
  const SavedPropertiesList = useMemo(() => {
    const availabilityFn = getPropertyExpiry;
    const fmt = formatExpiryMessage;
    const available = propertyList.filter((p) => {
      const created = p.created || p.created_at || p.createdAt || null;
      if (!availabilityFn) return true;
      const expiry = availabilityFn(created, (typeof userPlan !== 'undefined') ? (userPlan as any) : null);
      return !expiry.isExpired;
    });

    const isPro = (userPlan || "").toLowerCase().includes("pro");

    return (
      <div className="space-y-4">
        {/* Info Banner */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 sm:p-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-blue-900 text-sm sm:text-base">Property Access Window</p>
              <p className="text-xs sm:text-sm text-blue-800 mt-1">
                {!isPro ? (
                  <>
                    Saved launches stay available for <span className="font-semibold">60 days</span>.{" "}
                    <span className="font-semibold">Upgrade to Pro or Pro+</span> to extend access to{" "}
                    <span className="font-semibold">120 days</span>.
                  </>
                ) : (
                  <>
                    Saved launches stay available for <span className="font-semibold">60 days</span>. You have access to{" "}
                    <span className="font-semibold">120 days</span> with your {userPlan} plan.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {loadingProperties ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
            <p className="text-muted-foreground text-sm">Loading properties...</p>
          </div>
        ) : available.length === 0 ? (
          <div className="text-center py-12 bg-muted/20 rounded-lg border-2 border-dashed border-muted px-4">
            <Home className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-2 text-sm">No available properties found.</p>
            <p className="text-xs text-muted-foreground">
              Use "Add Manually" or "Import from MLS" to add a property.
            </p>
          </div>
        ) : (
          <div className="max-h-[400px] space-y-3 overflow-y-auto pr-0 sm:pr-2">
            {available.map((property) => {
              const isAutoAILoading = autoAILoadingPropertyId === property.id;
              const rawUrl = property.image ? getFullUrl(property.image) : null;
              const mainImage = rawUrl || null;
              const created = property.created || property.created_at || property.createdAt || null;
              const expiry = availabilityFn ? availabilityFn(created, userPlan ?? null) : null;

              return (
                <div
                  key={property.id}
                  className="rounded-lg border border-border bg-background p-3 transition-all duration-200 hover:border-primary/30 hover:shadow-md"
                >
                  {/* TOP ROW: image + info */}
                  <div className="flex gap-3 items-start mb-3">
                    {mainImage ? (
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                        <img
                          src={mainImage}
                          alt={property.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        <Home className="h-7 w-7 text-muted-foreground" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-semibold text-primary truncate mb-1">
                        {property.title || "Untitled Property"}
                      </h3>
                      <p className="text-sm font-semibold text-red-500 mb-1.5">
                        {property.price != null ? `$${property.price.toLocaleString()}` : "Price not set"}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground">
                        {property.bedrooms != null && <span>🛏️ {property.bedrooms} bed</span>}
                        {property.bathrooms != null && <span>🚿 {property.bathrooms} bath</span>}
                        {property.area != null && <span>📐 {property.area} sqft</span>}
                      </div>
                    </div>
                  </div>

                  {/* BADGES ROW */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <span className="rounded bg-secondary px-2 py-0.5 text-xs text-secondary-foreground whitespace-nowrap">
                      {property.property_type}
                    </span>
                    <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-800 whitespace-nowrap">
                      {property.status}
                    </span>
                    {(property.photos?.length || 0) > 0 && (
                      <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800 whitespace-nowrap">
                        📷 {(property.photos?.length || 0) + 1} photos
                      </span>
                    )}
                    {expiry && !expiry.isExpired && fmt && (
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800 font-medium whitespace-nowrap">
                        ⏰ {fmt(expiry.remainingDays)}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-3">
                    {/* Delete + Edit icons */}
                    <div className="flex gap-1.5 sm:flex-shrink-0">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); openDeleteDialog(String(property.id)); }}
                        className="h-9 w-9 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                        title="Delete property"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>

                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setEditProperty(property)}
                        className="h-9 w-9"
                        title="Edit property"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Auto IQ + Use This Property */}
                    <div className="flex flex-1 gap-1.5 min-w-0">
                      {/* Auto IQ */}
                      <div className="relative group flex-1 min-w-0">
                        <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gradient-to-r from-red-500 to-red-600 px-2.5 py-1 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                          Coming Soon
                        </span>
                        <Button
                          onClick={() => handleAutoAIClick(property)}
                          disabled={true || isAutoAILoading}
                          className="h-9 w-full text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 border border-gray-200 shadow-none font-normal"
                          title="Auto IQ"
                        >
                          {isAutoAILoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><span>✨</span><span className="ml-1">Auto IQ</span></>
                          )}
                        </Button>
                      </div>

                      {/* Use This Property */}
                      <Button
                        onClick={() => {
                          handlePropertySelect(property);
                          if (property.subject) setCampaignSubject(property.subject);
                        }}
                        className="h-9 flex-1 min-w-0 text-sm text-white bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 whitespace-nowrap"
                      >
                        Use This Property
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }, [propertyList, loadingProperties, handlePropertySelect, setCampaignSubject, handleAutoAIClick, autoAILoadingPropertyId, getFullUrl, userPlan, getPropertyExpiry, formatExpiryMessage]);

  // ✅ Property Info Card - Shows after successful search
  const PropertyInfoCard = useMemo(() => {
    if (!mlsSearchCompleted || !propertyData.address) return null;

    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-800">Property Found</p>
              <p className="text-sm text-green-600">
                Data populated from public records
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleResetForm}
            className="shrink-0 border-green-300 text-green-700 hover:bg-green-100"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            New Search
          </Button>
        </div>

        {/* Property Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-3 border-t border-green-200">
          {/* Price */}
          {propertyData.price && (
            <div className="text-center">
              <p className="text-2xl font-bold text-green-700">
                ${parseInt(propertyData.price).toLocaleString()}
              </p>
              <p className="text-xs text-green-600">List Price</p>
            </div>
          )}

          {/* Beds/Baths */}
          <div className="text-center">
            <p className="text-2xl font-bold text-green-700">
              {propertyData.bedrooms}/{propertyData.bathrooms}
            </p>
            <p className="text-xs text-green-600">Beds/Baths</p>
          </div>

          {/* Sqft */}
          {propertyData.sqft && (
            <div className="text-center">
              <p className="text-2xl font-bold text-green-700">
                {parseInt(propertyData.sqft).toLocaleString()}
              </p>
              <p className="text-xs text-green-600">Sq. Ft.</p>
            </div>
          )}

          {/* Year Built */}
          {additionalPropertyInfo.yearBuilt && (
            <div className="text-center">
              <p className="text-2xl font-bold text-green-700">
                {additionalPropertyInfo.yearBuilt}
              </p>
              <p className="text-xs text-green-600">Year Built</p>
            </div>
          )}
        </div>

        {/* Listing Tags */}
        {additionalPropertyInfo.listingTag && (
          <div className="flex flex-wrap gap-2 pt-3 border-t border-green-200">
            {additionalPropertyInfo.listingTag.split(",").map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800"
              >
                <Tag className="h-3 w-3" />
                {tag.trim()}
              </span>
            ))}
          </div>
        )}

        {/* Additional Details */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-3 border-t border-green-200 text-sm">
          {additionalPropertyInfo.county && (
            <div className="flex items-center gap-2 text-green-700">
              <MapPin className="h-4 w-4" />
              <span>{additionalPropertyInfo.county}</span>
            </div>
          )}

          {additionalPropertyInfo.mlsStatus && (
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-4 w-4" />
              <span>MLS: {additionalPropertyInfo.mlsStatus}</span>
            </div>
          )}

          {additionalPropertyInfo.daysOnMarket && (
            <div className="flex items-center gap-2 text-green-700">
              <AlertCircle className="h-4 w-4" />
              <span>{additionalPropertyInfo.daysOnMarket} days on market</span>
            </div>
          )}

          {additionalPropertyInfo.lotSqft && (
            <div className="flex items-center gap-2 text-green-700">
              <Building2 className="h-4 w-4" />
              <span>{parseInt(additionalPropertyInfo.lotSqft).toLocaleString()} sq ft lot</span>
            </div>
          )}

          {additionalPropertyInfo.stories && (
            <div className="flex items-center gap-2 text-green-700">
              <Home className="h-4 w-4" />
              <span>{additionalPropertyInfo.stories} story</span>
            </div>
          )}

          {additionalPropertyInfo.estimatedValue && (
            <div className="flex items-center gap-2 text-green-700">
              <DollarSign className="h-4 w-4" />
              <span>Est. Value: ${parseInt(additionalPropertyInfo.estimatedValue).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
    );
  }, [mlsSearchCompleted, propertyData, additionalPropertyInfo, handleResetForm]);

  // MLS Search Section
  const MLSSearchSection = useMemo(
    () => (
      <div className="space-y-4">
        {/* Search Input */}
        <div className="space-y-3">
          <Label htmlFor="mlsAddress">Property Address *</Label>
          <p className="text-sm text-muted-foreground">
            Enter the property address to search public records
          </p>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <AddressAutocomplete
                value={mlsAddress}
                onChange={setMlsAddress}
                placeholder="e.g., 2812 S Shine Ave, Orlando, FL"
                id="mlsAddress"
                required
                disabled={isSearchingMLS}
                className="pr-10"
                showLabel={false}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isSearchingMLS && mlsAddress.trim()) {
                    handleAddressSearch();
                  }
                }}
              />
              {/* <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10" /> */}
            </div>
            <Button
              type="button"
              variant="default"
              onClick={handleAddressSearch}
              disabled={isSearchingMLS || !mlsAddress.trim()}
              className="shrink-0 min-w-[120px]"
            >
              {isSearchingMLS ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Search typically takes 5-15 seconds
          </p>
        </div>

        {/* ✅ Property Info Card - Shows after successful search */}
        {/* {PropertyInfoCard} */}
      </div>
    ),
    [
      mlsAddress,
      isSearchingMLS,
      handleAddressSearch,
      PropertyInfoCard,
    ]
  );

  // Property Form
  const PropertyForm = useMemo(
    () => (
      <div className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-2">
          {/* LEFT COLUMN */}
          <div className="space-y-4">
            {/* Property Title */}
            <div className="space-y-2">
              <Label htmlFor="headline">Property Title *</Label>
              <Input
                id="headline"
                type="text"
                placeholder="Enter property title..."
                value={propertyData.headline}
                onChange={(e) => handleInputChange("headline", e.target.value)}
                onBlur={() => markTouched("headline")}
                autoComplete="off"
                className={fieldError("headline", propertyData.headline) ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}
              />
            </div>

            {/* Address */}
            <AddressAutocomplete
              value={propertyData.address}
              onChange={(value) => handleInputChange("address", value)}
              onBlur={() => markTouched("address")}
              id="address"
              required
              className={fieldError("address", propertyData.address) ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}
            />

            {/* Price */}
            <div className="space-y-2">
              <Label htmlFor="price" className="flex items-center gap-2">
                Price *
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="price"
                  type="text"
                  maxLength={12}
                  min={0}
                  inputMode="numeric"
                  placeholder="Enter price..."
                  value={propertyData.price}
                  onChange={(e) =>
                    handleIntegerChange("price", e.target.value, "Price")
                  }
                  onBlur={() => markTouched("price")}
                  className={`pl-10 ${fieldError("price", propertyData.price) ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : propertySource === "mls" && mlsSearchCompleted && !propertyData.price ? "border-border focus:border-border focus:ring-1 focus:ring-ring" : ""}`}
                />
              </div>
            </div>

            {/* Property List Type Selector */}
            {PropertyListTypeSelector}

            {/* Bedrooms, Bathrooms, Sqft */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="bedrooms">Bedrooms *</Label>
                <Input
                  id="bedrooms"
                  type="text"
                  maxLength={3}
                  inputMode="numeric"
                  placeholder="0"
                  value={propertyData.bedrooms}
                  onChange={(e) =>
                    handleIntegerChange("bedrooms", e.target.value, "Bedrooms")
                  }
                  onBlur={() => markTouched("bedrooms")}
                  className={fieldError("bedrooms", propertyData.bedrooms) ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bathrooms">Bathrooms *</Label>
                <Input
                  id="bathrooms"
                  type="text"
                  maxLength={4}
                  inputMode="decimal"
                  placeholder="0"
                  value={propertyData.bathrooms}
                  onChange={(e) =>
                    handleFloatChange("bathrooms", e.target.value)
                  }
                  onBlur={() => markTouched("bathrooms")}
                  className={fieldError("bathrooms", propertyData.bathrooms) ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sqft">Sq. Ft.</Label>
                <Input
                  id="sqft"
                  type="text"
                  maxLength={8}
                  inputMode="numeric"
                  placeholder="0"
                  value={propertyData.sqft}
                  onChange={(e) =>
                    handleIntegerChange("sqft", e.target.value, "Sq. Ft.")
                  }
                />
              </div>
            </div>

            {/* Property Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="propertyType">Property Type *</Label>
                <Select
                  value={propertyData.propertyType}
                  onValueChange={(value) => {
                    handleInputChange("propertyType", value);
                    markTouched("propertyType");
                  }}
                >
                  <SelectTrigger
                    id="propertyType"
                    onBlur={() => markTouched("propertyType")}
                    className={fieldError("propertyType", propertyData.propertyType) ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}
                  >
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6">
            {/* Open House Toggle */}
            <div className="mt-2">
              <Label>Open House</Label>
              <div className="flex items-center gap-3 mt-1">
                <Switch
                  checked={!!propertyData.is_open_house}
                  onCheckedChange={(c) =>
                    setPropertyData((prev) => ({
                      ...prev,
                      is_open_house: Boolean(c),
                    }))
                  }
                />
                <span className="text-sm text-muted-foreground">
                  {propertyData.is_open_house
                    ? "✅ Marked as Open House"
                    : "Mark property as Open House"}
                </span>
              </div>
              {additionalPropertyInfo.listingTag?.includes("Open House") && (
                <p className="text-xs text-green-600 mt-1">
                  ℹ️ Open House detected from MLS listing
                </p>
              )}
            </div>

            {/* Subject Line */}
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                placeholder="Enter email subject line..."
                value={propertyData.subject}
                onChange={(e) => handleInputChange("subject", e.target.value)}
                onBlur={() => markTouched("subject")}
                className={fieldError("subject", propertyData.subject) ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}
              />
              {subjectGenCount >= MAX_GENERATION_ATTEMPTS && (
                <span className="text-xs text-orange-600">Limit reached</span>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                rows={8}
                placeholder="Enter description..."
                value={propertyData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                onBlur={() => markTouched("description")}
                className={`resize-none${fieldError("description", propertyData.description) ? " border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]" : ""}`}
              />

              {/* Auto IQ Button */}
              <div className="flex flex-wrap gap-2 mt-2">
                <div className="relative group">
                  {/* <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gradient-to-r from-red-500 to-red-600 px-2.5 py-1 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                      Coming Soon
                    </span> */}
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAutoIQ}
                    disabled={isAutoIQLoading}
                    className="relative h-10 px-4 text-sm font-medium text-white bg-blue-500 hover:bg-blue-700 active:bg-red-800 disabled:bg-red-300 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2 shrink-0 transition-colors"
                  >
                    {isAutoIQLoading ? (
                      <>
                        <Sparkles className="h-3 w-3 animate-spin" />
                        Thinking...
                      </>
                    ) : (
                      <>✨ Auto IQ</>
                    )}
                  </Button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                💡 Type at least 10 characters to enable AI features. Click
                "Auto IQ" for an AI-powered description, or type your own.
              </p>
            </div>
          </div>
        </div>

        {/* KEY FEATURES SECTION - Full Width */}
        <div className="border-t pt-6">
          {KeyFeaturesSection}
        </div>
      </div>
    ),
    [
      // ✅ Original dependencies
      propertyData,
      propertySource,
      mlsSearchCompleted,
      isGeneratingDesc,
      generationCount,
      isAutoIQLoading,
      autoIQCount,
      subjectGenCount,
      handleInputChange,
      handleIntegerChange,
      handleAutoIQ,
      // ✅ NEW: Add these dependencies for the new components
      PropertyListTypeSelector,
      KeyFeaturesSection,
      setPropertyData, // ✅ ADD: For Open House switch
      markTouched,
      fieldError,
    ]
  );

  // Image Upload Section
  const ImageUploadSection = useMemo(() => {
    const canAddMore = photos.length < MAX_PHOTOS;
    const showMLSReminder =
      propertySource === "mls" && mlsSearchCompleted && photos.length === 0;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="flex items-center gap-2">Property Images *</Label>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload up to {MAX_PHOTOS} photos. First photo will be the main
              image.
            </p>
          </div>
          <span
            className={`text-sm font-medium ${photos.length >= 1 ? "text-green-600" : "text-muted-foreground"
              }`}
          >
            {photos.length}/{MAX_PHOTOS} photos
          </span>
        </div>

        {canAddMore && (
          <div
            ref={dropZoneRef}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`
              relative cursor-pointer rounded-lg border border-dashed p-8
              transition-all duration-200 ease-in-out
              ${isDragging
                ? "border-primary bg-primary/5 scale-[1.02]"
                : touchedFields.has("photos") && photos.length === 0
                  ? "border-red-300 bg-red-50/30 shadow-[0_0_0_3px_rgba(239,68,68,0.12)]"
                  : showMLSReminder
                    ? "border-blue-400 bg-white hover:border-blue-500 hover:bg-blue-100/50"
                    : "border-border bg-background hover:border-primary/50 hover:bg-muted/40"
              }
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />
            <div className="flex flex-col items-center justify-center text-center">
              <div
                className={`mb-4 rounded-full p-4 transition-colors ${isDragging
                  ? "bg-primary/10"
                  : showMLSReminder
                    ? "bg-amber-100"
                    : "bg-muted"
                  }`}
              >
                <Upload
                  className={`h-8 w-8 ${isDragging
                    ? "text-primary"
                    : showMLSReminder
                      ? "text-grey-600"
                      : "text-muted-foreground"
                    }`}
                />
              </div>
              <p className="text-base font-medium text-foreground">
                {isDragging
                  ? "Drop your photos here"
                  : "Drag & drop photos here"}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                or{" "}
                <span
                  className={`font-medium ${showMLSReminder ? "text-amber-600" : "text-primary"
                    }`}
                >
                  click to browse
                </span>
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                PNG, JPG up to 5 MB each • Max {MAX_PHOTOS - photos.length}{" "}
                more
              </p>
            </div>
          </div>
        )}

        {photos.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground">
                Uploaded Photos
              </Label>
              {photos.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  Click "Set as Main" to change the primary photo
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Please hover on image for enhance
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {photos.map((photo, index) => {
                const isEnhanced = enhancedPhotoIds.has(photo.id);

                return (
                  <div
                    key={photo.id}
                    className={`
                      group relative overflow-hidden rounded-xl transition-all duration-300
                      ${index === 0
                        ? "ring-2 ring-primary ring-offset-2 shadow-lg shadow-primary/20"
                        : "ring-1 ring-border hover:ring-2 hover:ring-primary/50 hover:shadow-md"
                      }
                    `}
                  >
                    {index === 0 && (
                      <div className="absolute left-2 top-2 z-20 flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground shadow-lg">
                        <CheckCircle2 className="h-3 w-3" />
                        Main
                      </div>
                    )}

                    {isEnhanced && (
                      <div className="absolute left-2 bottom-2 z-20 flex items-center gap-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 px-2.5 py-1 text-xs font-semibold text-white shadow-lg">
                        <Sparkles className="h-3 w-3" />
                        Enhanced
                      </div>
                    )}

                    <div className="absolute right-10 top-2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-xs font-bold text-white backdrop-blur-sm">
                      {index + 1}
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removePhoto(photo.id);
                      }}
                      className="
                        absolute right-2 top-2 z-30
                        flex h-7 w-7 items-center justify-center
                        rounded-full bg-red-500 text-white
                        shadow-lg ring-2 ring-white/80
                        transition-all duration-200
                        hover:bg-red-600 hover:scale-110 hover:ring-white
                        active:scale-95
                      "
                      title="Remove photo"
                    >
                      <X className="h-4 w-4" strokeWidth={2.5} />
                    </button>

                    <div className="aspect-[4/3] bg-muted">
                      <img
                        src={photo.url}
                        alt={`Property photo ${index + 1}`}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>

                    <div
                      className="
                        absolute inset-0 z-10
                        flex flex-col items-center justify-center gap-3
                        bg-gradient-to-t from-black/80 via-black/50 to-transparent
                        opacity-0 transition-all duration-300
                        group-hover:opacity-100
                      "
                    >
                      {photo.file && !isEnhanced && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEnhanceImage(photo);
                          }}
                          className="
                            flex items-center gap-2
                            rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600
                            px-4 py-2.5 text-sm font-semibold text-white
                            shadow-lg shadow-blue-500/30
                            transition-all duration-200
                            hover:from-blue-600 hover:to-indigo-700 hover:scale-105 hover:shadow-xl
                            active:scale-95
                          "
                        >
                          <Sparkles className="h-4 w-4" />
                          Auto AI
                        </button>
                      )}
                      {/* 
                      {isEnhanced && (
                        <div className="flex items-center gap-2 rounded-lg bg-green-600/90 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
                          <CheckCircle2 className="h-4 w-4" />
                          Already Enhanced
                        </div>
                      )} */}

                      {index !== 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAsMainPhoto(photo.id);
                          }}
                          className="
                            flex items-center gap-2
                            rounded-lg bg-white/20 backdrop-blur-md
                            px-4 py-2 text-sm font-medium text-white
                            border border-white/30
                            transition-all duration-200
                            hover:bg-white/30 hover:scale-105
                            active:scale-95
                          "
                        >
                          <ImageIcon className="h-4 w-4" />
                          Set as Main
                        </button>
                      )}
                    </div>

                    {index === 0 && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary to-primary/60" />
                    )}
                  </div>
                );
              })}

              {canAddMore && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted/30 transition-all hover:border-primary/50 hover:bg-muted/50"
                >
                  <Plus className="mb-1 h-6 w-6 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    Add Photo
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }, [
    photos,
    isDragging,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    fileInputRef,
    handleFileInputChange,
    removePhoto,
    setAsMainPhoto,
    enhancedPhotoIds,
    handleEnhanceImage,
    propertySource,
    mlsSearchCompleted,
    touchedFields,
  ]);

  // Validation Section
  const ValidationSection = useMemo(() => {
    const validationItems = getValidationItems();
    const completedCount = validationItems.filter(
      (item) => item.isValid
    ).length;
    const totalCount = validationItems.length;
    const isComplete = completedCount === totalCount;

    return (
      <div
        className={`rounded-lg border p-4 transition-colors ${isComplete
          ? "border-green-200 bg-green-50"
          : "border-border bg-muted/30"
          }`}
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-primary">Validation Status</h4>
          <span
            className={`text-sm font-medium ${isComplete ? "text-green-600" : "text-muted-foreground"
              }`}
          >
            {completedCount}/{totalCount} complete
          </span>
        </div>
        <div className="grid gap-2 text-sm sm:grid-cols-2">
          {validationItems.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              {item.isValid ? (
                <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span
                className={
                  item.isValid ? "text-foreground" : "text-muted-foreground"
                }
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }, [getValidationItems]);

  // MLS Empty State
  const MLSEmptyState = useMemo(
    () => (
      <div className="py-12">
        <div className="text-center text-muted-foreground">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
            <Building2 className="h-10 w-10 opacity-50" />
          </div>
          <p className="text-lg font-medium text-foreground">
            No property loaded
          </p>
          <p className="mt-2 text-sm max-w-sm mx-auto">
            Enter a property address above to search public records and
            automatically populate property details.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <Home className="h-4 w-4" />
              <span>Property Details</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              <span>Address Info</span>
            </div>
            <div className="flex items-center gap-1.5">
              <DollarSign className="h-4 w-4" />
              <span>Value Data</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ImageIcon className="h-4 w-4" />
              <span>Property Images</span>
            </div>
          </div>
        </div>
      </div>
    ),
    []
  );

  // Enhancement Dialog Component
  const EnhancementDialog = useMemo(
    () => (
      <Dialog
        open={enhanceDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEnhanceDialogOpen(false);
            setEnhanceDialogPhoto(null);
            setOriginalImageUrl(null);
            setEnhancedPreviewUrl(null);
            setIsEnhancingPreview(false);
            // ✅ CRITICAL FIX: Clear enhanced photo tracking when dialog closes (user cancelled)
            // This prevents the enhancement from being applied if user didn't click Apply
            if (enhanceDialogPhoto) {
              setEnhancedPhotoIds((prev) => {
                const updated = new Set(prev);
                updated.delete(enhanceDialogPhoto.id);
                return updated;
              });
            }
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                <Sparkles className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">
                  AI Image Enhancement Preview
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Compare before and after AI enhancement
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="p-6 space-y-6">
            {isEnhancingPreview ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="relative">
                  <div className="h-20 w-20 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Sparkles className="h-8 w-8 text-blue-600 animate-pulse" />
                  </div>
                </div>
                <p className="mt-6 text-lg font-medium text-foreground">
                  Enhancing your image...
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  This may take a few seconds
                </p>

                {originalImageUrl && (
                  <div className="mt-6 p-2 bg-muted rounded-lg">
                    <img
                      src={originalImageUrl}
                      alt="Processing"
                      className="w-32 h-24 object-cover rounded opacity-60"
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-foreground flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-gray-400" />
                        Original Image
                      </h3>
                    </div>
                    <div className="relative rounded-xl overflow-hidden border-2 border-gray-300 bg-muted min-h-[200px]">
                      {originalImageUrl ? (
                        <>
                          <img
                            src={originalImageUrl}
                            alt="Original"
                            className="w-full h-auto max-h-[450px] object-contain"
                          />
                          <div className="absolute top-3 left-3 bg-gray-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold">
                            Before
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                          <AlertCircle className="h-16 w-16 mb-3 opacity-30" />
                          <p className="text-sm">
                            Original image not available
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-foreground flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                        Enhanced Image
                      </h3>
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        AI Enhanced
                      </span>
                    </div>
                    <div className="relative rounded-xl overflow-hidden border-2 border-green-500 bg-muted shadow-lg shadow-green-500/20 min-h-[200px]">
                      {enhancedPreviewUrl ? (
                        <>
                          <img
                            src={enhancedPreviewUrl}
                            alt="Enhanced"
                            className="w-full h-auto max-h-[450px] object-contain"
                          />
                          <div className="absolute top-3 left-3 bg-green-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5">
                            <Sparkles className="h-3 w-3" />
                            After
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                          <AlertCircle className="h-16 w-16 mb-3 opacity-30" />
                          <p className="text-sm">
                            Enhancement preview not available
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 flex-shrink-0">
                      <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                        AI Enhancement Applied
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm text-blue-700 dark:text-blue-300">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span>Improved lighting & contrast</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span>Enhanced color accuracy</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span>Reduced noise & artifacts</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span>Sharpened details</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => {
                      // ✅ CRITICAL FIX: Clear enhancement state when user cancels
                      // Remove photo from enhancedPhotoIds so it doesn't get applied
                      if (enhanceDialogPhoto) {
                        setEnhancedPhotoIds((prev) => {
                          const updated = new Set(prev);
                          updated.delete(enhanceDialogPhoto.id);
                          return updated;
                        });
                      }

                      setEnhanceDialogOpen(false);
                      setEnhanceDialogPhoto(null);
                      setOriginalImageUrl(null);
                      setEnhancedPreviewUrl(null);
                    }}
                    className="px-6"
                  >
                    Cancel
                  </Button>

                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8"
                    onClick={handleApplyEnhancement}
                    disabled={!enhancedPreviewUrl}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    Apply Enhancement
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    ),
    [
      enhanceDialogOpen,
      enhanceDialogPhoto,
      originalImageUrl,
      enhancedPreviewUrl,
      isEnhancingPreview,
      handleApplyEnhancement,
    ]
  );

  // ==========================================
  // MAIN RENDER
  // ==========================================

  const MLSSearchLoaderComponent = useMemo(
    () => (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <Card className="w-full max-w-md mx-4 shadow-2xl border-0">
          <CardContent className="pt-8 pb-8">
            <div className="flex flex-col items-center space-y-6">
              <div className="relative">
                <div className="h-20 w-20 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Database className="h-8 w-8 text-primary animate-pulse" />
                </div>
              </div>

              <div className="text-center space-y-2">
                <h3 className="text-xl font-semibold text-foreground">
                  Searching Property Records
                </h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Looking up "
                  <span className="font-medium text-foreground">
                    {mlsAddress}
                  </span>
                  "...
                </p>
              </div>

              <div className="flex items-center gap-2 text-sm text-primary">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="animate-pulse">{loadingMessage}</span>
              </div>

              <div className="w-full space-y-2">
                <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500 ease-out"
                    style={{ width: `${searchProgress}%` }}
                  />
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  This may take 15-30 seconds
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    ),
    [mlsAddress, loadingMessage, searchProgress]
  );

  const DeleteConfirmationDialog = useMemo(
    () => (
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Delete Property
            </DialogTitle>
            <DialogDescription className="text-base">
              Are you sure for delete that property. The property and
              all its photos will be permanently removed.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={closeDeleteDialog}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    ),
    [deleteDialogOpen, deleting, closeDeleteDialog, confirmDelete]
  );
  // ==========================================
  // MAIN RENDER
  // ==========================================
  return (
    <div className="mx-auto w-full max-w-6xl px-4">
      {isSearchingMLS && MLSSearchLoaderComponent}

      {/* <h2 className="text-2xl font-bold text-primary mb-8 text-center">
        Select or Add a Property
      </h2> */}

      <Card className="w-full">
        <CardContent className="space-y-6 p-4 pt-6">
          {/* Property Source Selector */}
          {PropertySourceSelector}

          <hr className="border-border" />

          {/* Saved Properties */}
          {propertySource === "saved" && SavedPropertiesList}

          {/* MLS Import */}
          {/* MLS Import - Button Section */}
          {propertySource === "mls" && (
            <>
              {MLSSearchSection}

              {propertyData.address || propertyData.headline ? (
                <>
                  <hr className="border-border" />
                  {PropertyForm}
                  {ImageUploadSection}
                  {ValidationSection}

                  <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:flex-wrap sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleResetForm}
                      disabled={isSaving || isAutoAIProcessing}
                      className="w-full sm:w-auto"
                    >
                      Reset
                    </Button>

                    {/* AUTO IQ Button for MLS */}
                    {/* AUTO IQ Button for MLS */}
                    <div className="relative group w-full sm:w-auto">
                      <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gradient-to-r from-red-500 to-red-600 px-2.5 py-1 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                        Coming Soon
                      </span>
                      <Button
                        type="button"
                        size="lg"
                        disabled={true}
                        className="w-full sm:w-auto bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-200 shadow-none font-normal"
                      >
                        <>✨ Auto IQ</>
                      </Button>
                    </div>

                    <Button
                      type="button"
                      variant="default"
                      size="lg"
                      onClick={handleAddPropertyAndSelect}
                      disabled={isSaving || isAutoAIProcessing}
                      className="w-full bg-primary hover:bg-primary/90 sm:w-auto"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Plus className="mr-2 h-4 w-4" />
                          Add & Use This Property
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                MLSEmptyState
              )}
            </>
          )}

          {/* Manual Entry */}
          {propertySource === "manual" && (
            <>
              {PropertyForm}
              {ImageUploadSection}
              {ValidationSection}

              <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:flex-wrap sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResetForm}
                  disabled={isSaving || isAutoAIProcessing}
                  className="w-full sm:w-auto"
                >
                  Reset
                </Button>

                {/* AUTO IQ Button for Manual Entry */}
                <div className="relative group w-full sm:w-auto">
                  <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gradient-to-r from-red-500 to-red-600 px-2.5 py-1 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50">
                    Coming Soon
                  </span>
                  <Button
                    type="button"
                    size="lg"
                    disabled={true}
                    className="w-full sm:w-auto bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-200 shadow-none font-normal"
                  >
                    <>✨ Auto IQ</>
                  </Button>
                </div>

                <Button
                  type="button"
                  variant="default"
                  size="lg"
                  onClick={handleAddPropertyAndSelect}
                  disabled={isSaving || isAutoAIProcessing}
                  className="w-full bg-primary hover:bg-primary/90 sm:w-auto"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Add & Use This Property
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Back Button */}
      <div className="flex justify-start mt-6">
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>

      {/* EDIT PROPERTY DIALOG */}
      <Dialog
        open={!!editProperty}
        onOpenChange={(open) => {
          if (!open) {
            setEditProperty(null);
            setEditPhotos([]);
            mainPhotoChangedRef.current = false;
            setIsUpdatingProperty(false); // ← ADD THIS
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <Edit className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">
                  Edit Property
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Update all property information
                </p>
              </div>
            </div>
          </DialogHeader>

          {editForm && (
            <div className="p-6 space-y-8">
              {/* Subject Line Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-border">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold text-foreground">
                    Email Subject Line
                  </h4>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-subject" className="text-sm font-medium">
                    Subject <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="edit-subject"
                    required
                    placeholder="Enter email subject line..."
                    value={editForm.subject || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, subject: e.target.value })
                    }
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground">
                    This subject line will be used when sending launch emails
                  </p>
                </div>
              </div>

              {/* Basic Information Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-border">
                  <Home className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold text-foreground">
                    Basic Information
                  </h4>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="edit-title" className="text-sm font-medium">
                      Property Title <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="edit-title"
                      placeholder="Enter property title..."
                      required
                      value={editForm.title}
                      onChange={(e) =>
                        setEditForm({ ...editForm, title: e.target.value })
                      }
                      className="h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="edit-address"
                      className="text-sm font-medium"
                    >
                      Address <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="edit-address"
                        placeholder="Enter full address..."
                        required
                        value={editForm.address}
                        onChange={(e) =>
                          setEditForm({ ...editForm, address: e.target.value })
                        }
                        className="h-11 pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-price" className="text-sm font-medium">
                      Price <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="edit-price"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min={0}
                        placeholder="Enter price..."
                        required
                        value={editForm.price || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            price: e.target.value === "" ? 0 : Number(e.target.value),
                          })
                        }
                        className="h-11 pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-type" className="text-sm font-medium">
                      Property Type <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={editForm.property_type}
                      onValueChange={(value) =>
                        setEditForm({ ...editForm, property_type: value })
                      }
                    >
                      <SelectTrigger id="edit-type" className="h-11">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Property Details Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-border">
                  <Building2 className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold text-foreground">
                    Property Details
                  </h4>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="edit-bedrooms"
                      className="text-sm font-medium"
                    >
                      Bedrooms <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="edit-bedrooms"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min={0}
                        placeholder="0"
                        value={editForm.bedrooms || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            bedrooms: e.target.value === "" ? 0 : Number(e.target.value),
                          })
                        }
                        className="h-11 text-center pr-10"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lg">
                        🛏️
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="edit-bathrooms"
                      className="text-sm font-medium"
                    >
                      Bathrooms <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="edit-bathrooms"
                        type="text"
                        maxLength={4}
                        inputMode="decimal"
                        pattern="^\d*\.?\d*$"
                        min={0}
                        placeholder="0"
                        value={editForm.bathrooms ?? ""}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          if (rawValue === "" || /^\d*\.?\d*$/.test(rawValue)) {
                            setEditForm({
                              ...editForm,
                              bathrooms: rawValue,
                            });
                          }
                        }}
                        className="h-11 text-center pr-10"
                      />

                    
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lg">
                        🚿
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-area" className="text-sm font-medium">
                      Area (Sq.Ft) <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="edit-area"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min={0}
                        placeholder="0"
                        value={editForm.area || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            area: e.target.value === "" ? 0 : Number(e.target.value),
                          })
                        }
                        className="h-11 text-center pr-10"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lg">
                        📐
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Open House</Label>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={!!editForm.is_open_house}
                      onCheckedChange={(c) =>
                        setEditForm((prev) => ({
                          ...prev!,
                          is_open_house: Boolean(c),
                        }))
                      }
                    />
                    <span className="text-sm text-muted-foreground">
                      Mark property as Open House
                    </span>
                  </div>
                </div>
              </div>

              {/* Description Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-border">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold text-foreground">
                    Property Description
                  </h4>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="edit-description"
                    className="text-sm font-medium"
                  >
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="edit-description"
                    rows={6}
                    required
                    placeholder="Enter property description..."
                    value={editForm.description || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, description: e.target.value })
                    }
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    Provide a detailed description of the property features and
                    highlights
                  </p>
                </div>
              </div>

              {/* Photos Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-border">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5 text-primary" />
                    <h4 className="font-semibold text-foreground">
                      Property Photos
                    </h4>
                  </div>
                  <span
                    className={`text-sm font-medium ${editPhotos.length >= 1
                      ? "text-green-600"
                      : "text-muted-foreground"
                      }`}
                  >
                    {editPhotos.length}/{MAX_PHOTOS} photos
                  </span>
                </div>

                {/* Upload Zone */}
                {editPhotos.length < MAX_PHOTOS && (
                  <div
                    ref={editDropZoneRef}
                    onDragEnter={handleEditDragEnter}
                    onDragOver={handleEditDragOver}
                    onDragLeave={handleEditDragLeave}
                    onDrop={handleEditDrop}
                    onClick={() => editFileInputRef.current?.click()}
                    className={`
                relative cursor-pointer rounded-lg border border-dashed p-6
                transition-all duration-200 ease-in-out
                ${isEditDragging
                        ? "border-primary bg-primary/5 scale-[1.02]"
                        : "border-border bg-background hover:border-primary/50 hover:bg-muted/40"
                      }
              `}
                  >
                    <input
                      ref={editFileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleEditFileInputChange}
                      className="hidden"
                    />
                    <div className="flex flex-col items-center justify-center text-center">
                      <div
                        className={`mb-3 rounded-full p-3 transition-colors ${isEditDragging ? "bg-primary/10" : "bg-muted"
                          }`}
                      >
                        <Upload
                          className={`h-6 w-6 ${isEditDragging
                            ? "text-primary"
                            : "text-muted-foreground"
                            }`}
                        />
                      </div>
                      <p className="text-sm font-medium text-foreground">
                        {isEditDragging
                          ? "Drop your photos here"
                          : "Drag & drop photos or click to browse"}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        PNG, JPG up to 5 MB • Max{" "}
                        {MAX_PHOTOS - editPhotos.length} more
                      </p>
                    </div>
                  </div>
                )}

                {/* Photo Grid */}
                {editPhotos.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {editPhotos.map((photo, index) => (
                      <div
                        key={photo.id}
                        className={`
                    group relative overflow-hidden rounded-lg transition-all duration-300
                    ${index === 0
                            ? "ring-2 ring-primary ring-offset-2 shadow-md"
                            : "ring-1 ring-border hover:ring-2 hover:ring-primary/50"
                          }
                  `}
                      >
                        {index === 0 && (
                          <div className="absolute left-2 top-2 z-20 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground shadow-md">
                            <CheckCircle2 className="h-3 w-3" />
                            Main
                          </div>
                        )}

                        <div className="absolute right-8 top-2 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-xs font-bold text-white">
                          {index + 1}
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeEditPhoto(photo.id);
                          }}
                          className="absolute right-2 top-2 z-30 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow-md transition-all hover:bg-red-600 hover:scale-110"
                          title="Remove photo"
                        >
                          <X className="h-3 w-3" strokeWidth={2.5} />
                        </button>

                        <div className="aspect-[4/3] bg-muted">
                          <img
                            src={photo.url}
                            alt={`Property photo ${index + 1}`}
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                          />
                        </div>

                        {index !== 0 && (
                          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditAsMainPhoto(photo.id);
                              }}
                              className="flex items-center gap-2 rounded-md bg-white/90 px-3 py-1.5 text-sm font-medium text-foreground transition-all hover:bg-white"
                            >
                              <ImageIcon className="h-3 w-3" />
                              Set as Main
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    {editPhotos.length < MAX_PHOTOS && (
                      <div
                        onClick={() => editFileInputRef.current?.click()}
                        className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/30 transition-all hover:border-primary/50 hover:bg-muted/50"
                      >
                        <Plus className="mb-1 h-5 w-5 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground">
                          Add Photo
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditProperty(null);
                    setEditPhotos([]);
                    setIsUpdatingProperty(false); // ← ADD THIS
                  }}
                  size="lg"
                >
                  Cancel
                </Button>

                <Button
                  className="bg-primary text-white hover:bg-primary/90"
                  onClick={handleUpdateProperty}
                  disabled={isUpdatingProperty}
                  size="lg"
                >
                  {isUpdatingProperty ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {DeleteConfirmationDialog}


      {/* IMAGE ENHANCEMENT DIALOG */}
      {EnhancementDialog}
    </div>
  );
};

export default PropertyStep;
